/* eslint-disable react-hooks/rules-of-hooks */
// src/components/ads-admin/AdsMatrixView.jsx
// Salesforce-style ads browse view with list/matrix toggle, column filters, date range, pagination.

import React, { useState, useEffect } from "react";
import Chip from "./Chip";
import VisualAttributesPanel from "./VisualAttributesPanel";
import AdsCoveragePanel from "./AdsCoveragePanel";
import FieldPicker, { formatFieldValue, readField, OriginTag } from "./FieldPicker";
import AdvancedFieldsPanel from "./AdvancedFieldsPanel";
import { toneFromStatus } from "./utils";
import { adsGetCreativeSnapshot, adsRefreshCreativeSnapshot, adsAnalyzeAd, adsGetAdAnalyses, adsListFields, adsStartImageDownload, adsImageDownloadStatus, adsImageDownloadFile, adsGetAd, adsRefetchAd, adsAdVersions, adsAdVersionsDiff, adsGetCreativeVideo, adsFetchCreativeVideo } from "../../services/api";
import FieldFilterBuilder, { compactFieldFilters } from "./FieldFilterBuilder";
import {
  providerMeta,
  formatImpressionsRange,
  audienceFlagLabels,
  regionsSummary,
  isDirectImageUrl,
} from "./providerMeta";

// Tile label -> catalogue key, so each tile's origin tag is read from the
// registry rather than decided here.
const TILE_KEY = {
  "External ID": "external_ad_id", "Source": "source", "Advertiser": "advertiser_name",
  "Status": "status", "Start date": "start_date", "End date": "end_date", "Last seen": "end_date",
  "Total days shown": "total_days_shown", "Topic": "topic",
  "Impressions (range)": "impressions_lower", "Audience signals": "audience_selection_flags",
  "Top regions": "region_metrics_json", "EU reach": "eu_total_reach",
  "Platforms": "publisher_platforms", "Target ages": "target_ages", "Target gender": "target_gender",
  "Target locations": "target_locations", "Languages": "languages", "Paid by": "beneficiary_payers",
  "Type": "creative_type", "Headline": "headline", "Primary text": "primary_text",
  "Description": "description", "Call to action": "call_to_action", "Caption": "caption",
  "Landing URL": "landing_url",
};

// ─── thumbnail cache (module-level, survives re-renders) ─────────────────────
const _thumbCache = new Map(); // creativeId → data URL string or "error"

// ─── helpers ────────────────────────────────────────────────────────────────

function truncate(s, n = 80) {
  if (!s) return "-";
  return s.length > n ? s.slice(0, n) + "…" : s;
}

function fmtDate(x) {
  if (!x) return "-";
  const d = new Date(x);
  if (isNaN(d)) return String(x);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// ─── SnapshotThumb ────────────────────────────────────────────────────────────

function SnapshotThumb({ creativeId, imgStyle, loadingNode, errorNode, onTypeDetected }) {
  const [src, setSrc] = useState(() => _thumbCache.get(creativeId) || null);
  const [loading, setLoading] = useState(!src);
  useEffect(() => {
    if (src || !creativeId) return;
    let cancelled = false;
    adsGetCreativeSnapshot(creativeId, { cachedOnly: true })
      .then(data => {
        if (cancelled) return;
        const url = data?.image || "error";
        _thumbCache.set(creativeId, url);
        setSrc(url);
        if (data?.creative_type) onTypeDetected?.(creativeId, data.creative_type);
      })
      .catch(() => { if (!cancelled) { _thumbCache.set(creativeId, "error"); setSrc("error"); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [creativeId, src]); // eslint-disable-line react-hooks/exhaustive-deps
  if (loading) return loadingNode ?? <span style={{ color: "#d1d5db", fontSize: 11 }}>…</span>;
  if (!src || src === "error") return errorNode ?? <span style={{ color: "#d1d5db" }}>-</span>;
  return <img src={src} alt="" style={{ maxHeight: 56, maxWidth: 80, borderRadius: 4, display: "block", objectFit: "contain", ...imgStyle }} />;
}

// ─── field definitions ───────────────────────────────────────────────────────

// Column definitions now come from GET /ads/fields; see FieldPicker.


// ─── sub-components ─────────────────────────────────────────────────────────

function FilterBar({
  filterDateFrom, setFilterDateFrom,
  filterDateTo, setFilterDateTo,
  filterEndDateFrom, setFilterEndDateFrom,
  filterEndDateTo, setFilterEndDateTo,
  filterStatus, setFilterStatus,
  filterCreativeType, setFilterCreativeType,
  filterHeadline, setFilterHeadline,
  filterAdvertiser, setFilterAdvertiser,
  filterAdvertiserName, setFilterAdvertiserName,
  filterCta, setFilterCta,
  filterBody, setFilterBody,
  filterVisualPalette, setFilterVisualPalette,
  filterVisualFunnel, setFilterVisualFunnel,
  filterVisualClaim, setFilterVisualClaim,
  filterPeoplePresent, setFilterPeoplePresent,
  filterNewDays, setFilterNewDays,
  filterPublisherPlatform, setFilterPublisherPlatform,
  filterLanguage, setFilterLanguage,
  filterTargetGender, setFilterTargetGender,
  filterEuReachMin, setFilterEuReachMin,
  filterTargetDomain, setFilterTargetDomain,
  filterGooglePlatform, setFilterGooglePlatform,
  filterBrandRole, setFilterBrandRole,
  competitors,
  onApply, onClear,
  savedFilters, onSaveFilter, onApplySavedFilter, onDeleteSavedFilter,
  catalog, fieldFilters, setFieldFilters, brandId, source,
  filterAsOf, setFilterAsOf, filterNotUpdatedSince, setFilterNotUpdatedSince,
  filterRunningFrom, setFilterRunningFrom, filterRunningTo, setFilterRunningTo,
}) {
  const [saveInputOpen, setSaveInputOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [savedDropdownOpen, setSavedDropdownOpen] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const stable = (o) => JSON.stringify(o, Object.keys(o || {}).sort());
  const current = stable(compactFieldFilters(fieldFilters));
  const activeFilter = (savedFilters || []).find(sf => {
    const f = sf.filters || {};
    return f.fieldFilters
      && stable(compactFieldFilters(f.fieldFilters)) === current
      && (f.newDays || "") === (filterNewDays || "")
      && (f.asOf || "") === (filterAsOf || "")
      && (f.notUpdatedSince || "") === (filterNotUpdatedSince || "")
      && (f.runningFrom || "") === (filterRunningFrom || "")
      && (f.runningTo || "") === (filterRunningTo || "");
  }) || null;
  const inputStyle = {
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    padding: "6px 10px",
    fontSize: 13,
    fontWeight: 600,
    color: "#111827",
    background: "#fff",
    outline: "none",
    minWidth: 130,
  };
  const labelStyle = { fontSize: 11, fontWeight: 800, color: "#6b7280", marginBottom: 4, display: "block", textTransform: "uppercase", letterSpacing: "0.05em" };

  return (
    <div style={{
      background: "#f8fafc",
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      padding: "12px 16px",
      display: "flex",
      flexWrap: "wrap",
      gap: 12,
      alignItems: "flex-end",
    }}>
      <FieldFilterBuilder
        catalog={catalog}
        value={fieldFilters || {}}
        onChange={setFieldFilters}
        brandId={brandId}
        source={source}
        includeCompetitors={(competitors || []).length > 0}
      />

      {/* A period the ads were live in. Storage keeps every ad ever fetched, so
          without this a period view also showed older ads. */}
      <div>
        <label style={labelStyle} title="Ads that were running at any point in this period, whenever they started. Use it to compare advertisers over the same period.">
          Running between
        </label>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input type="date" style={inputStyle} value={filterRunningFrom || ""} onChange={e => setFilterRunningFrom(e.target.value)} />
          <span style={{ color: "#9ca3af", fontSize: 12 }}>and</span>
          <input type="date" style={inputStyle} value={filterRunningTo || ""} onChange={e => setFilterRunningTo(e.target.value)} />
        </div>
      </div>

      {/* Versions: every run that fetched an ad kept a copy of it. */}
      <div>
        <label style={labelStyle} title="Show every ad as it was on this date: the version captured by then. Ads first fetched after it are left out.">
          As of
        </label>
        <input type="date" style={inputStyle} value={filterAsOf || ""} onChange={e => setFilterAsOf(e.target.value)} />
      </div>
      <div>
        <label style={labelStyle} title="Only ads that no run has fetched after this date, so their stored version is at least this old.">
          Not updated since
        </label>
        <input type="date" style={inputStyle} value={filterNotUpdatedSince || ""} onChange={e => setFilterNotUpdatedSince(e.target.value)} />
      </div>

      {/* Views of our own records rather than catalogue fields. */}
      <div>
        <label style={labelStyle}>New</label>
        <select style={{ ...inputStyle, minWidth: 110 }} value={filterNewDays || ""} onChange={e => setFilterNewDays(e.target.value)}>
          <option value="">Any time</option>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
        </select>
      </div>
      {(competitors || []).length > 0 && (
        <div>
          <label style={labelStyle}>Brand</label>
          <select style={{ ...inputStyle, minWidth: 140 }} value={filterBrandRole} onChange={e => setFilterBrandRole(e.target.value)}>
            <option value="">All</option>
            <option value="primary">Primary</option>
            {(competitors || []).map(c => (
              <option key={c.competitor_brand_id} value={String(c.competitor_brand_id)}>
                {c.competitor_brand_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Actions row: Apply / Clear / Saved dropdown / Save as */}
      <div style={{ width: "100%", borderTop: "1px solid #e5e7eb", paddingTop: 10, marginTop: 2, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <button className="btn-primary" onClick={onApply} style={{ padding: "8px 14px", borderRadius: 8, fontSize: 13 }}>
          Apply
        </button>
        <button className="btn-secondary" onClick={onClear} style={{ padding: "8px 14px", borderRadius: 8, fontSize: 13 }}>
          Clear
        </button>

        {/* Saved filters dropdown */}
        <div style={{ position: "relative" }}>
          <button
            onClick={() => setSavedDropdownOpen(v => !v)}
            style={{
              padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: activeFilter ? 800 : 600,
              background: activeFilter ? "#ede9fe" : "#fff",
              border: `1px solid ${activeFilter ? "#c4b5fd" : "#e5e7eb"}`,
              color: activeFilter ? "#5b21b6" : "#374151",
              cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
            }}
          >
            {activeFilter ? activeFilter.name : "Saved filters"} ▾
          </button>
          {savedDropdownOpen && (
            <div style={{
              position: "absolute", top: "calc(100% + 6px)", left: 0,
              background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
              boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
              zIndex: 600, minWidth: 220, padding: "4px 0",
            }}>
              {(savedFilters || []).length === 0 && (
                <div style={{ padding: "10px 16px", fontSize: 12, color: "#9ca3af" }}>No saved filters yet</div>
              )}
              {(savedFilters || []).map(sf => (
                <div
                  key={sf.id}
                  style={{ display: "flex", alignItems: "center", background: sf.id === activeFilter?.id ? "#ede9fe" : "transparent" }}
                  onMouseEnter={e => { if (sf.id !== activeFilter?.id) e.currentTarget.style.background = "#f8fafc"; }}
                  onMouseLeave={e => { if (sf.id !== activeFilter?.id) e.currentTarget.style.background = "transparent"; }}
                >
                  <button
                    onClick={() => { onApplySavedFilter(sf); setSavedDropdownOpen(false); }}
                    style={{
                      flex: 1, textAlign: "left", background: "none", border: "none",
                      padding: "9px 16px", fontSize: 13,
                      fontWeight: sf.id === activeFilter?.id ? 800 : 600,
                      color: sf.id === activeFilter?.id ? "#5b21b6" : "#111827",
                      cursor: "pointer",
                    }}
                  >{sf.name}</button>
                  {confirmDeleteId === sf.id ? (
                    <span style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 8px" }}>
                      <button
                        onClick={() => { onDeleteSavedFilter(sf.id); setConfirmDeleteId(null); }}
                        style={{ padding: "3px 9px", borderRadius: 6, fontSize: 12, fontWeight: 700, background: "#fee2e2", border: "1px solid #fca5a5", color: "#dc2626", cursor: "pointer" }}
                      >Delete</button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        style={{ padding: "3px 8px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: "none", border: "1px solid #e5e7eb", color: "#6b7280", cursor: "pointer" }}
                      >Cancel</button>
                    </span>
                  ) : (
                    <button
                      onClick={() => setConfirmDeleteId(sf.id)}
                      title="Delete"
                      style={{ background: "none", border: "none", padding: "9px 14px", cursor: "pointer", fontSize: 15, color: "#9ca3af", lineHeight: 1 }}
                    >×</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Save as */}
        {saveInputOpen ? (
          <>
            <input
              autoFocus
              type="text"
              placeholder="Filter name…"
              value={saveName}
              onChange={e => setSaveName(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && saveName.trim()) { onSaveFilter(saveName.trim()); setSaveName(""); setSaveInputOpen(false); }
                if (e.key === "Escape") { setSaveInputOpen(false); setSaveName(""); }
              }}
              style={{ border: "1px solid #c4b5fd", borderRadius: 8, padding: "6px 10px", fontSize: 13, fontWeight: 600, outline: "none", minWidth: 160 }}
            />
            <button
              onClick={() => { if (saveName.trim()) { onSaveFilter(saveName.trim()); setSaveName(""); setSaveInputOpen(false); } }}
              disabled={!saveName.trim()}
              style={{ padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 700, background: "#6366f1", color: "#fff", border: "none", cursor: saveName.trim() ? "pointer" : "not-allowed", opacity: saveName.trim() ? 1 : 0.5 }}
            >Save</button>
            <button onClick={() => { setSaveInputOpen(false); setSaveName(""); }} style={{ padding: "8px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600, background: "none", border: "1px solid #e5e7eb", cursor: "pointer", color: "#6b7280" }}>Cancel</button>
          </>
        ) : (
          <button
            onClick={() => setSaveInputOpen(true)}
            disabled={!!activeFilter}
            title={activeFilter ? "Modify filters to save a new preset" : "Save current filters as a preset"}
            style={{
              padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 700,
              background: activeFilter ? "#f3f4f6" : "#f0fdf4",
              border: `1px solid ${activeFilter ? "#e5e7eb" : "#bbf7d0"}`,
              color: activeFilter ? "#9ca3af" : "#15803d",
              cursor: activeFilter ? "not-allowed" : "pointer",
            }}
          >Save as…</button>
        )}
      </div>
    </div>
  );
}

function ViewToggle({ viewMode, setViewMode }) {
  const btn = (mode, label, icon) => (
    <button
      onClick={() => setViewMode(mode)}
      style={{
        padding: "7px 14px",
        borderRadius: 8,
        border: "1px solid",
        borderColor: viewMode === mode ? "#6366f1" : "#e5e7eb",
        background: viewMode === mode ? "#6366f1" : "#fff",
        color: viewMode === mode ? "#fff" : "#374151",
        fontWeight: 800,
        fontSize: 13,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      <span>{icon}</span>
      {label}
    </button>
  );
  return (
    <div style={{ display: "flex", gap: 4 }}>
      {btn("list", "List", "☰")}
      {btn("matrix", "Matrix", "⊞")}
    </div>
  );
}

function Pagination({ offset, limit, total, onPrev, onNext, onOffsetChange }) {
  const page = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit) || 1;
  const [inputPage, setInputPage] = React.useState(String(page));

  // Keep input in sync with external offset changes
  React.useEffect(() => { setInputPage(String(page)); }, [page]);

  const goToPage = () => {
    const n = parseInt(inputPage, 10);
    if (!isNaN(n) && onOffsetChange) {
      const clamped = Math.max(1, Math.min(n, totalPages));
      onOffsetChange((clamped - 1) * limit);
    }
    setInputPage(String(Math.max(1, Math.min(parseInt(inputPage, 10) || 1, totalPages))));
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, fontWeight: 700, color: "#374151" }}>
      <button className="btn-secondary" onClick={onPrev} disabled={offset === 0} style={{ padding: "6px 12px", borderRadius: 8 }}>
        ← Prev
      </button>
      <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
        Page
        <input
          type="number"
          min={1}
          max={totalPages}
          value={inputPage}
          onChange={e => setInputPage(e.target.value)}
          onBlur={goToPage}
          onKeyDown={e => e.key === "Enter" && goToPage()}
          style={{ width: 52, textAlign: "center", padding: "3px 6px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13, fontWeight: 700 }}
        />
        of {totalPages} &nbsp;·&nbsp; {total} total
      </span>
      <button className="btn-secondary" onClick={onNext} disabled={offset + limit >= total} style={{ padding: "6px 12px", borderRadius: 8 }}>
        Next →
      </button>
    </div>
  );
}

// ─── Creative preview (inline rendering) ────────────────────────────────────

function CreativePreview({ creative, compact = false }) {
  const [imgErr, setImgErr] = useState(false);
  const [expanded, setExpanded] = useState(false);

  if (!creative) return <span style={{ color: "#9ca3af", fontSize: 12 }}>-</span>;

  const { creative_type, media_url, headline, primary_text, call_to_action, caption } = creative;

  const isVideo = creative_type === "video" || (media_url && /\.(mp4|webm|ogg|mov)/i.test(media_url));
  const isImage = creative_type === "image" || creative_type === "carousel" || (media_url && /\.(jpg|jpeg|png|gif|webp)/i.test(media_url));

  if (compact) {
    // In list/matrix rows - show a small thumbnail badge
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {media_url && isImage && !imgErr && (
          <img
            src={media_url}
            alt=""
            onError={() => setImgErr(true)}
            style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 6, border: "1px solid #e5e7eb", flexShrink: 0 }}
          />
        )}
        {media_url && isVideo && (
          <span style={{ fontSize: 18 }}>🎬</span>
        )}
        <span style={{ fontSize: 12, fontWeight: 700, color: "#6366f1", textTransform: "uppercase" }}>
          {creative_type || "-"}
        </span>
      </div>
    );
  }

  // Full preview (used in detail drawer)
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden", background: "#fff" }}>
      {/* Media */}
      {media_url && isImage && !imgErr && (
        <img
          src={media_url}
          alt={headline || "Ad creative"}
          onError={() => setImgErr(true)}
          style={{ width: "100%", maxHeight: 280, objectFit: "contain", background: "#f9fafb" }}
        />
      )}
      {media_url && isVideo && (
        <video
          src={media_url}
          controls
          style={{ width: "100%", maxHeight: 280, background: "#000" }}
        />
      )}
      {/* Snapshot URLs (e.g. Facebook) are handled by SnapshotPreview above - no iframe fallback */}

      {/* Copy */}
      {(headline || primary_text || call_to_action || caption) && (
        <div style={{ padding: "12px 14px", display: "grid", gap: 6 }}>
          {headline && <div style={{ fontWeight: 900, fontSize: 14, color: "#111827" }}>{headline}</div>}
          {primary_text && (
            <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.5 }}>
              {expanded ? primary_text : truncate(primary_text, 120)}
              {primary_text.length > 120 && (
                <button
                  onClick={() => setExpanded(x => !x)}
                  style={{ background: "none", border: "none", color: "#6366f1", cursor: "pointer", fontWeight: 800, fontSize: 12, marginLeft: 4 }}
                >
                  {expanded ? "Show less" : "Show more"}
                </button>
              )}
            </div>
          )}
          {caption && <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700 }}>{caption}</div>}
          {call_to_action && (
            <div style={{ marginTop: 4 }}>
              <span style={{ background: "#6366f1", color: "#fff", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 900 }}>
                {call_to_action}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Snapshot preview (Playwright-captured PNG) ─────────────────────────────

// Compute the right "view ad publicly" URL per provider.
// For Meta: media_url is the now-dead render_ad endpoint; the public
// surface that still works is /ads/library/?id=<archive_id>. We
// derive that from the ad's external_ad_id (= archive id).
// For Google + others: fall back to media_url unchanged.
function _publicViewUrl({ source, mediaUrl, externalAdId }) {
  if (source === "meta" && externalAdId) {
    // The search filters are required, not cosmetic. A bare ?id= inherits the
    // Ad Library's defaults (active ads, one country), which most archived ads
    // fail, so the page opens on "no ads found" behind the ad's own dialog.
    return (
      "https://www.facebook.com/ads/library/" +
      "?active_status=all&ad_type=all&country=ALL&media_type=all" +
      `&search_type=page&id=${externalAdId}`
    );
  }
  if (source === "tiktok" && externalAdId) {
    return `https://library.tiktok.com/ads/detail/?ad_id=${externalAdId}`;
  }
  return mediaUrl;
}

// A TikTok creative's video, from our stored copy. TikTok's own links expire
// within hours, so the file is stored when the ad is collected; when it was
// not (disk floor, a failed download), one details call fetches it.
function StoredVideo({ creative }) {
  const [state, setState] = useState("idle"); // idle | loading | ready | error
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => () => { if (src) URL.revokeObjectURL(src); }, [src]);

  const play = async (fetchFirst) => {
    setError(null);
    setState("loading");
    try {
      if (fetchFirst) await adsFetchCreativeVideo(creative.id);
      setSrc(await adsGetCreativeVideo(creative.id));
      setState("ready");
    } catch (e) {
      setError(e?.detail?.message || e?.message || "The video could not be loaded.");
      setState("error");
    }
  };

  if (state === "ready" && src) {
    return (
      <video src={src} controls autoPlay
        style={{ width: "100%", maxHeight: 480, background: "#000", borderRadius: 10, marginBottom: 8 }} />
    );
  }
  const stored = !!creative.video_stored;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
      <button className="btn-secondary" disabled={state === "loading"} onClick={() => play(!stored)}
        title={stored ? "Play the stored copy" : "Fetches the video from TikTok and stores it. Uses one SearchAPI credit."}>
        {state === "loading" ? "Loading video…" : stored ? "▶ Play video" : "▶ Fetch and play video"}
      </button>
      {!stored && state !== "loading" && (
        <span style={{ fontSize: 11.5, color: "#6b7280" }}>Not stored yet; one SearchAPI credit.</span>
      )}
      {error && <span style={{ fontSize: 12, color: "#b91c1c", fontWeight: 700 }}>{error}</span>}
    </div>
  );
}

function SnapshotPreview({ creativeId, mediaUrl, onSnapshotData, source, externalAdId, onCaptured }) {
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [errInfo, setErrInfo] = useState(null);
  // Creatives are tall portraits; at full-screen width they would otherwise
  // fill the panel. Remembered so the choice survives opening the next ad.
  const [previewWidth, setPreviewWidth] = useState(() => {
    const v = parseInt(localStorage.getItem("ads.preview.width") || "100", 10);
    return Number.isFinite(v) && v >= 25 && v <= 100 ? v : 100;
  });
  const [imgSrc, setImgSrc] = useState(null);
  const [lightbox, setLightbox] = useState(false);

  // Only show for snapshot/page URLs - skip direct image/video files
  const isDirect = !mediaUrl || /\.(jpg|jpeg|png|gif|webp|mp4|webm|ogg|mov)/i.test(mediaUrl);
  if (isDirect) return null;

  const load = async (forceRefresh = false, probe = false) => {
    setErrInfo(null);
    setState("loading");
    try {
      const res = (forceRefresh || probe)
        ? await adsRefreshCreativeSnapshot(creativeId, { probe })
        : await adsGetCreativeSnapshot(creativeId);
      // Bust the inner-image cache-buster (CRA's HMR may cache base64
      // srcs in dev). The data URI changes anyway when re-captured.
      setImgSrc(res.image);
      // Also invalidate the table-thumbnail cache so the matrix row's
      // small preview updates next time it re-renders.
      _thumbCache.delete(creativeId);
      onSnapshotData?.({ landing_url: res.landing_url, creative_type: res.creative_type, call_to_action: res.call_to_action });
      // The card in the list was rendered before this image existed.
      onCaptured?.(creativeId);
      setState("done");
    } catch (e) {
      // Keep what the server said. A provider-wide block, a removed ad and a
      // bug are three different situations and used to look identical here.
      setErrInfo({
        message: e?.detail?.message || e?.message || "The preview could not be captured.",
        reason: e?.detail?.reason || "error",
        retryAfter: e?.detail?.retry_after_seconds || 0,
      });
      setState("error");
    }
  };

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
        {state === "idle" && (
          <button
            className="btn-secondary"
            onClick={() => load(false)}
            style={{ fontSize: 13, padding: "8px 14px", borderRadius: 8 }}
          >
            📸 Capture Preview
          </button>
        )}
        {state === "done" && (
          <button
            className="btn-secondary"
            onClick={() => load(true)}
            style={{ fontSize: 12, padding: "5px 10px", borderRadius: 6, color: "#6b7280" }}
            title="Bust the cache and run the snapshot again (useful for stale Meta tokens or after improvements to the capture logic)"
          >
            ↻ Recapture
          </button>
        )}
        {state === "done" && imgSrc && (
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "#6b7280", fontWeight: 700 }}>
            Size
            <input
              id={`preview-size-${creativeId}`}
              type="range"
              min={25}
              max={100}
              step={5}
              value={previewWidth}
              onChange={e => {
                const v = Number(e.target.value);
                setPreviewWidth(v);
                try { localStorage.setItem("ads.preview.width", String(v)); } catch {}
              }}
              style={{ width: 90, accentColor: "#6366f1", cursor: "pointer" }}
            />
            <span style={{ fontVariantNumeric: "tabular-nums", width: 30 }}>{previewWidth}%</span>
          </label>
        )}
        <a
          href={_publicViewUrl({ source, mediaUrl, externalAdId })}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: 12, color: "#6366f1", fontWeight: 800, textDecoration: "none" }}
          title={providerMeta(source).mediaCtaTitle}
        >
          {providerMeta(source).mediaCtaLabel} ↗
        </a>
      </div>
      {state === "loading" && (
        <div style={{ color: "#6b7280", fontSize: 13, fontWeight: 700, padding: "8px 0" }}>
          Capturing preview… (takes a few seconds)
        </div>
      )}
      {state === "error" && (
        <div style={{
          fontSize: 12, fontWeight: 600, lineHeight: 1.5,
          color: errInfo?.reason === "provider_blocked" ? "#92400e" : "#b91c1c",
          background: errInfo?.reason === "provider_blocked" ? "#fffbeb" : "#fef2f2",
          border: "1px solid",
          borderColor: errInfo?.reason === "provider_blocked" ? "#fde68a" : "#fecaca",
          borderRadius: 8, padding: "9px 11px", whiteSpace: "pre-wrap",
        }}>
          {errInfo?.message || "The preview could not be captured."}
          {errInfo?.reason === "provider_blocked" ? (
            // Bulk retrying is what prolongs a block, but a person checking
            // whether it has lifted is not that, and the local hold expiring
            // proves nothing either way. So offer exactly one deliberate check.
            <div style={{ marginTop: 8 }}>
              <button
                onClick={() => load(true, true)}
                style={{
                  border: "1px solid #fcd34d", background: "#fff", color: "#92400e",
                  borderRadius: 7, padding: "5px 11px", cursor: "pointer", boxShadow: "none", transform: "none",
                  fontSize: 12, fontWeight: 800,
                }}
              >
                Check again now
              </button>
              <div style={{ marginTop: 5, fontWeight: 600, opacity: 0.85 }}>
                Sends one request to see whether the provider has relented.
                {errInfo.retryAfter > 0
                  ? ` Automatic captures stay held for about ${Math.max(1, Math.round(errInfo.retryAfter / 60))} min.`
                  : ""}
              </div>
            </div>
          ) : (
          <button
            onClick={() => load(true)}
            style={{ background: "none", border: "none", color: "#6366f1", cursor: "pointer", fontWeight: 800, marginTop: 4, padding: 0 }}
          >
            Retry
          </button>
          )}
        </div>
      )}
      {state === "done" && imgSrc && (
        <>
          <img
            src={imgSrc}
            alt="Ad preview"
            onClick={() => setLightbox(true)}
            style={{
              width: `${previewWidth}%`, maxWidth: "100%",
              borderRadius: 10, border: "1px solid #e5e7eb",
              cursor: "zoom-in", display: "block",
            }}
            title="Click to enlarge"
          />
          {lightbox && (
            <div
              onClick={() => setLightbox(false)}
              style={{
                position: "fixed", inset: 0, zIndex: 9999,
                background: "rgba(0,0,0,0.85)",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "zoom-out",
              }}
            >
              <img
                src={imgSrc}
                alt="Ad preview (full size)"
                style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 12, boxShadow: "0 24px 60px rgba(0,0,0,0.5)" }}
                onClick={(e) => e.stopPropagation()}
              />
              <button
                onClick={() => setLightbox(false)}
                style={{
                  position: "fixed", top: 20, right: 24,
                  background: "rgba(255,255,255,0.15)", border: "none", color: "#fff",
                  fontSize: 24, borderRadius: 8, padding: "4px 12px", cursor: "pointer",
                }}
              >
                ✕
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Creative detail item (holds snapshot-discovered state) ─────────────────

function CreativeDetailItem({ creative: c, index: i, source, ad, onCaptured }) {
  const [discovered, setDiscovered] = useState({});

  const onSnapshotData = (data) => {
    setDiscovered(prev => ({
      landing_url: data.landing_url || prev.landing_url,
      creative_type: data.creative_type || prev.creative_type,
      call_to_action: data.call_to_action || prev.call_to_action,
    }));
  };

  const landingUrl = discovered.landing_url || c.landing_url;
  const cta = discovered.call_to_action || c.call_to_action;
  const cType = discovered.creative_type || c.creative_type;

  // Tiles, matching the ad-level fields: a creative has ten short values and
  // one long one, and ten full-width rows is what made this column endless.
  const field = (label, value) => (
    <div style={{
      border: "1px solid #eef2f7", borderRadius: 8, padding: "7px 10px",
      background: "#fff", minWidth: 0,
      gridColumn: typeof value === "string" && value.length > 70 ? "1 / -1" : "auto",
    }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
        <OriginTag origin={ad?.field_origins?.[TILE_KEY[label]]} />
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", wordBreak: "break-word", marginTop: 2 }}>
        {value || "-"}
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 800, color: "#9ca3af", marginBottom: 6 }}>Creative #{i + 1}</div>
      <SnapshotPreview creativeId={c.id} mediaUrl={c.media_url} onSnapshotData={onSnapshotData} source={source} externalAdId={ad?.external_ad_id} onCaptured={onCaptured} />
      {source === "tiktok" && (c.video_stored || c.variation_meta_json?.media?.has_video) && <StoredVideo creative={c} />}
      {source === "tiktok" && !c.media_url && (
        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
          TikTok published this ad without its image or video. Its text, dates, audience and targeting are below.
        </div>
      )}
      {c.id != null && <VisualAttributesPanel creativeId={c.id} isAdmin />}
      {source === "tiktok" && ad?.external_ad_id && (
        <div style={{ marginBottom: 8 }}>
          <a
            href={`https://library.tiktok.com/ads/detail/?ad_id=${ad.external_ad_id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 12, color: "#6366f1", fontWeight: 800, textDecoration: "none" }}
          >
            View on TikTok Ad Library ↗
          </a>
        </div>
      )}
      {source === "google" && ad?.advertiser_ref && ad?.external_ad_id && (
        <div style={{ marginBottom: 8 }}>
          <a
            href={`https://adstransparency.google.com/advertiser/${ad.advertiser_ref}/creative/${ad.external_ad_id}?region=RO`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 12, color: "#6366f1", fontWeight: 800, textDecoration: "none" }}
          >
            View on Google Ads Transparency ↗
          </a>
        </div>
      )}
      <CreativePreview creative={{ ...c, creative_type: cType, media_url: source === "tiktok" ? null : c.media_url }} compact={false} />
      <div style={{
        display: "grid", gap: 6, marginTop: 8,
        gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", alignItems: "start",
      }}>
        {field("Type", cType ? (
          <span style={{ background: "#f0fdf4", color: "#166534", border: "1px solid #bbf7d0", borderRadius: 6, padding: "2px 8px", fontSize: 12, fontWeight: 800, textTransform: "uppercase" }}>
            {cType}
          </span>
        ) : null)}
        {field("Headline", c.headline)}
        {field("Primary text", c.primary_text)}
        {field("Description", c.description)}
        {field("Call to action", cta)}
        {field("Caption", c.caption)}
        {field("Landing URL", landingUrl ? (
          <a href={landingUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#6366f1", wordBreak: "break-all" }}>
            {truncate(landingUrl, 60)}
          </a>
        ) : null)}
      </div>
      <CarouselCards creative={c} />
    </div>
  );
}

/**
 * Every card of a carousel, not just the first.
 *
 * The creative row holds card 1; the rest live in variation_meta_json.cards
 * and were only ever used for the "N cards" badge, so four of five headlines
 * of a carousel were in the database and nowhere on screen.
 */
function CarouselCards({ creative }) {
  const cards = creative?.variation_meta_json?.cards;
  if (!Array.isArray(cards) || cards.length < 2) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 6 }}>
        Carousel cards ({cards.length})
        <OriginTag origin="api" />
      </div>
      <div style={{ display: "grid", gap: 6 }}>
        {cards.map((card, i) => {
          const parts = [card.primary_text, card.description, card.caption].filter(Boolean);
          const cardImg = creative?.variation_meta_json?.media?.cards?.[i]?.image_url
            || creative?.variation_meta_json?.media?.cards?.[i]?.video_preview_url;
          return (
            <div key={card.index ?? i} style={{ display: "flex", gap: 9, alignItems: "baseline", border: "1px solid #eef2f7", borderRadius: 8, padding: "7px 10px", background: "#fff" }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: "#94a3b8", flexShrink: 0 }}>{(card.index ?? i) + 1}</span>
              {cardImg && (
                <img src={cardImg} alt="" style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6, flexShrink: 0 }} />
              )}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: card.headline ? "#111827" : "#cbd5e1" }}>
                  {card.headline || "No headline"}
                </div>
                {parts.map((t, j) => (
                  <div key={j} style={{ fontSize: 12.5, color: "#475569", marginTop: 2 }}>{t}</div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── AI Analysis panel ───────────────────────────────────────────────────────

function AnalysisPanel({ data }) {
  const field = (label, value) => value ? (
    <div style={{ display: "flex", gap: 8, paddingBottom: 7, borderBottom: "1px solid #f0f9f4" }}>
      <div style={{ minWidth: 120, fontSize: 11, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", paddingTop: 1 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", flex: 1, wordBreak: "break-word", lineHeight: 1.45 }}>{value}</div>
    </div>
  ) : null;
  const list = (label, items, itemStyle) => Array.isArray(items) && items.length > 0 ? (
    <div style={{ display: "flex", gap: 8, paddingBottom: 7, borderBottom: "1px solid #f0f9f4" }}>
      <div style={{ minWidth: 120, fontSize: 11, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", paddingTop: 1 }}>{label}</div>
      <div style={{ flex: 1 }}>
        {items.map((item, i) => (
          <div key={i} style={{ fontSize: 13, fontWeight: 600, color: itemStyle?.color || "#111827", marginBottom: 2, lineHeight: 1.4 }}>• {item}</div>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <div style={{ background: "#f0fdf4", borderRadius: 10, padding: 14, border: "1px solid #bbf7d0", display: "grid", gap: 7, marginTop: 4 }}>
      {data.summary && (
        <p style={{ margin: "0 0 6px", fontSize: 13, color: "#166534", fontWeight: 600, lineHeight: 1.55 }}>{data.summary}</p>
      )}
      {field("Tone", data.tone)}
      {field("Main message", data.main_message)}
      {field("Audience", data.target_audience)}
      {field("CTA", data.cta_analysis)}
      {list("Key claims", data.key_claims)}
      {list("Flags", data.flags, { color: "#b91c1c" })}
    </div>
  );
}

// ─── Ad detail drawer content ────────────────────────────────────────────────

const HISTORY_PAGE_SIZE = 5;

const RUN_KIND_LABEL = { collect: "Fetch", monitor: "Monitor", refetch: "Refetch" };

function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? String(iso) : d.toLocaleString(undefined, {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

const CHANGE_LABEL = {
  status: "Status", start_date: "First seen", end_date: "Last seen",
  eu_total_reach: "EU reach", publisher_platforms: "Platforms", languages: "Languages",
  target_ages: "Target ages", target_gender: "Target gender", topic: "Topic",
  total_days_shown: "Days shown", impressions_lower: "Impressions, low",
  impressions_upper: "Impressions, high", target_domain: "Destination domain",
  page_name: "Page name", advertiser_name: "Advertiser", creatives_count: "Number of creatives",
  "creative.creative_type": "Creative type", "creative.headline": "Headline",
  "creative.primary_text": "Body text", "creative.description": "Description",
  "creative.call_to_action": "Call to action", "creative.caption": "Caption",
  "creative.landing_url": "Landing page", "creative.media_url": "Creative source",
  "creative.variation_meta_json": "Carousel cards", "creative.duration_seconds": "Duration",
  other_creatives: "Other creatives",
  total_reach_by_location: "Reach by location", age_country_gender_reach_breakdown: "Reach by age, country, gender",
  target_locations: "Target locations", beneficiary_payers: "Payer and beneficiary",
  region_metrics_json: "Regions", audience_selection_flags: "Targeting used",
  platforms: "Google platforms", details_link: "Transparency page",
  impressions_data_available_from: "Impressions available from", ad_creation_time: "Created",
  political_metrics_json: "Political metrics", page_id: "Page ID",
};

function fmtChangeValue(v) {
  if (v === null || v === undefined || v === "") return "empty";
  if (Array.isArray(v)) return v.join(", ") || "empty";
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return fmtDateTime(s);
  return s.length > 60 ? s.slice(0, 57) + "…" : s;
}

/**
 * Where the ad sits in the collection: the run that added it, the run that
 * last saw or updated it, how often it has been collected, and a refetch.
 *
 * The collection holds every ad ever collected for the brand; a run is one
 * execution against the provider. "Added by" never changes. "Last updated by"
 * moves with every collection or refetch that sees the ad again.
 */
function CollectionSection({ ad, brandId, onRefetched, onShowRun, sectionHead }) {
  const [state, setState] = useState("idle"); // idle | running | done | error
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const col = ad.collection || {};
  const added = col.added_by_run;
  const last = col.last_run;

  const refetch = async () => {
    setState("running");
    setError(null);
    try {
      const out = await adsRefetchAd(ad.id, brandId);
      setResult(out);
      setState("done");
      onRefetched?.(ad.id);
    } catch (e) {
      setError(e?.detail?.message || e?.message || "Refetch failed.");
      setState("error");
    }
  };

  const runLine = (run) => {
    if (!run) return <span style={{ color: "#94a3b8" }}>Not recorded (collected before runs were tracked)</span>;
    return (
      <span>
        {onShowRun ? (
          <span
            role="button"
            tabIndex={0}
            onClick={() => onShowRun(run.id)}
            onKeyDown={e => { if (e.key === "Enter") onShowRun(run.id); }}
            title="Show every ad this run added to storage"
            style={{ color: "#4338ca", fontWeight: 800, cursor: "pointer", textDecoration: "underline" }}
          >
            Run #{run.id}
          </span>
        ) : <b>Run #{run.id}</b>}
        {" · "}{RUN_KIND_LABEL[run.kind] || run.kind}
        {run.started_at ? " · " + fmtDateTime(run.started_at) : ""}
      </span>
    );
  };

  const row = (label, value) => (
    <div style={{ display: "flex", gap: 10, fontSize: 13, lineHeight: 1.5 }}>
      <span style={{ color: "#94a3b8", fontWeight: 700, minWidth: 118, flexShrink: 0 }}>{label}</span>
      <span style={{ color: "#111827", minWidth: 0 }}>{value}</span>
    </div>
  );

  return (
    <section>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
        <h4 style={{ ...sectionHead, margin: 0 }}>In storage <OriginTag origin="internal" /></h4>
        <button
          className="btn-secondary"
          onClick={refetch}
          disabled={state === "running"}
          title="Fetch this ad again from the provider and update it in place. Recorded as its own run."
          style={{ padding: "6px 12px", borderRadius: 8, fontSize: 12.5 }}
        >
          {state === "running" ? "Refetching…" : "↻ Refetch ad"}
        </button>
      </div>
      <div style={{ display: "grid", gap: 4, border: "1px solid #eef2f7", borderRadius: 10, padding: "10px 12px", background: "#fff" }}>
        {row("Added by", runLine(added))}
        {row("Last updated by", last && added && last.id === added.id
          ? <span style={{ color: "#64748b" }}>Same run. Not collected again since.</span>
          : runLine(last))}
        {row("Collected", col.seen_in_runs ? `${col.seen_in_runs} time${col.seen_in_runs === 1 ? "" : "s"}` : "-")}
      </div>

      {state === "done" && result && (
        <div style={{
          marginTop: 8, borderRadius: 8, padding: "9px 11px", fontSize: 12.5, lineHeight: 1.5,
          background: result.found ? "#f0fdf4" : "#fffbeb",
          border: `1px solid ${result.found ? "#bbf7d0" : "#fde68a"}`,
          color: result.found ? "#166534" : "#92400e",
        }}>
          {!result.found && (result.message || "The provider no longer returns this ad. Nothing was changed.")}
          {result.found && !(result.changes || []).length && (
            <>Refetched in run #{result.run_id}. The provider returned the same data; nothing changed.</>
          )}
          {result.found && (result.changes || []).length > 0 && (
            <>
              <div style={{ fontWeight: 800, marginBottom: 4 }}>
                Refetched in run #{result.run_id}. {result.changes.length} field{result.changes.length === 1 ? "" : "s"} changed:
              </div>
              <div style={{ display: "grid", gap: 2 }}>
                {result.changes.map(c => (
                  <div key={c.field}>
                    <b>{CHANGE_LABEL[c.field] || c.field}</b>: {fmtChangeValue(c.before)} → {fmtChangeValue(c.after)}
                  </div>
                ))}
              </div>
            </>
          )}
          {result.extraction_enqueued > 0 && (
            <div style={{ marginTop: 4, color: "#475569" }}>
              {result.extraction_enqueued} creative image{result.extraction_enqueued === 1 ? "" : "s"} queued for reading.
            </div>
          )}
        </div>
      )}
      {state === "error" && (
        <div style={{ marginTop: 8, borderRadius: 8, padding: "9px 11px", fontSize: 12.5, background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", whiteSpace: "pre-wrap" }}>
          {error}
        </div>
      )}
    </section>
  );
}

/**
 * Every version of the ad: one per run that fetched it.
 *
 * Pick one to see the whole page as that version recorded it, or compare two
 * to see exactly what changed between them. The stored ad is never touched;
 * viewing an old version only changes what this page shows.
 */
function VersionsSection({ ad, sectionHead, viewingNo, onView }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [cmpA, setCmpA] = useState("");
  const [cmpB, setCmpB] = useState("");
  const [diff, setDiff] = useState(null);
  const [diffErr, setDiffErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    adsAdVersions(ad.id)
      .then(d => {
        if (cancelled) return;
        const list = d.items || [];
        setItems(list);
        if (list.length >= 2) {
          setCmpA(String(list[list.length - 2].version_no));
          setCmpB(String(list[list.length - 1].version_no));
        }
      })
      .catch(e => { if (!cancelled) setError(e?.message || "Could not load versions."); });
    return () => { cancelled = true; };
  }, [ad.id, ad.latest_version]);

  const compare = async () => {
    setDiff(null);
    setDiffErr(null);
    try {
      const a = Math.min(Number(cmpA), Number(cmpB));
      const b = Math.max(Number(cmpA), Number(cmpB));
      setDiff(await adsAdVersionsDiff(ad.id, a, b));
    } catch (e) {
      setDiffErr(e?.message || "Could not compare.");
    }
  };

  const latestNo = items?.length ? items[items.length - 1].version_no : null;
  const selectStyle = { padding: "5px 8px", borderRadius: 7, border: "1px solid #d1d5db", fontSize: 12.5 };

  return (
    <section>
      <h4 style={sectionHead}>
        Versions {items ? `(${items.length})` : ""} <OriginTag origin="internal" />
      </h4>
      {error && <div style={{ fontSize: 12.5, color: "#b91c1c" }}>{error}</div>}
      {items === null && !error && <div style={{ fontSize: 12.5, color: "#9ca3af" }}>Loading versions…</div>}
      {items && items.length === 0 && (
        <div style={{ fontSize: 12.5, color: "#9ca3af" }}>No versions yet. The next fetch of this ad records one.</div>
      )}
      {items && items.length > 0 && (
        <div style={{ border: "1px solid #eef2f7", borderRadius: 10, background: "#fff", maxHeight: 260, overflowY: "auto" }}>
          {[...items].reverse().map(v => {
            const active = (viewingNo ?? latestNo) === v.version_no;
            return (
              <div
                key={v.version_no}
                role="button"
                tabIndex={0}
                onClick={() => onView(v.version_no === latestNo ? null : v.version_no)}
                onKeyDown={e => { if (e.key === "Enter") onView(v.version_no === latestNo ? null : v.version_no); }}
                style={{
                  display: "flex", gap: 10, alignItems: "baseline", padding: "7px 12px", cursor: "pointer",
                  borderBottom: "1px solid #f3f4f6", background: active ? "#eef2ff" : "transparent",
                }}
              >
                <span style={{ fontWeight: 800, fontSize: 13, color: active ? "#4338ca" : "#111827", minWidth: 34 }}>v{v.version_no}</span>
                <span style={{ fontSize: 12.5, color: "#374151", minWidth: 150 }}>{fmtDateTime(v.captured_at)}</span>
                <span style={{ fontSize: 12, color: "#6b7280", minWidth: 90 }}>
                  {v.is_baseline ? "Starting version" : (RUN_KIND_LABEL[v.run_kind] || "Run")}{v.run_id ? ` #${v.run_id}` : ""}
                </span>
                <span style={{ fontSize: 12, color: v.changed_fields.length ? "#b45309" : "#9ca3af", minWidth: 0 }}>
                  {v.version_no === items[0].version_no
                    ? "first version"
                    : v.changed_fields.length
                      ? `${v.changed_fields.length} change${v.changed_fields.length === 1 ? "" : "s"}: ${v.changed_fields.map(f => CHANGE_LABEL[f] || f).join(", ")}`
                      : "no changes"}
                </span>
                {v.version_no === latestNo && <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 800, color: "#047857" }}>latest</span>}
              </div>
            );
          })}
        </div>
      )}

      {items && items.length >= 2 && (
        <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 12.5, color: "#374151", fontWeight: 700 }}>
          Compare
          <select value={cmpA} onChange={e => setCmpA(e.target.value)} style={selectStyle}>
            {items.map(v => <option key={v.version_no} value={v.version_no}>v{v.version_no}</option>)}
          </select>
          with
          <select value={cmpB} onChange={e => setCmpB(e.target.value)} style={selectStyle}>
            {items.map(v => <option key={v.version_no} value={v.version_no}>v{v.version_no}</option>)}
          </select>
          <button className="btn-secondary" onClick={compare} disabled={cmpA === cmpB}
                  style={{ padding: "5px 11px", borderRadius: 7, fontSize: 12.5 }}>
            Show differences
          </button>
        </div>
      )}
      {diffErr && <div style={{ marginTop: 6, fontSize: 12.5, color: "#b91c1c" }}>{diffErr}</div>}
      {diff && (
        <div style={{ marginTop: 8, border: "1px solid #eef2f7", borderRadius: 10, background: "#fff", padding: "8px 12px" }}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: "#111827", marginBottom: 6 }}>
            v{diff.a.version_no} ({fmtDateTime(diff.a.captured_at)}) → v{diff.b.version_no} ({fmtDateTime(diff.b.captured_at)})
          </div>
          {diff.changes.length === 0 ? (
            <div style={{ fontSize: 12.5, color: "#6b7280" }}>No differences. The provider returned the same data both times.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
              <thead>
                <tr style={{ textAlign: "left", color: "#6b7280" }}>
                  <th style={{ padding: "4px 6px", fontWeight: 800 }}>Field</th>
                  <th style={{ padding: "4px 6px", fontWeight: 800 }}>v{diff.a.version_no}</th>
                  <th style={{ padding: "4px 6px", fontWeight: 800 }}>v{diff.b.version_no}</th>
                </tr>
              </thead>
              <tbody>
                {diff.changes.map(c => (
                  <tr key={c.field} style={{ borderTop: "1px solid #f3f4f6", verticalAlign: "top" }}>
                    <td style={{ padding: "5px 6px", fontWeight: 700, color: "#111827" }}>{CHANGE_LABEL[c.field] || c.field}</td>
                    <td style={{ padding: "5px 6px", color: "#991b1b", wordBreak: "break-word" }}>{fmtChangeValue(c.before)}</td>
                    <td style={{ padding: "5px 6px", color: "#166534", wordBreak: "break-word" }}>{fmtChangeValue(c.after)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}

export function AdDetailContent({ ad: adProp, loading, brandId, onRefetched, onShowRun }) {
  // A refetch replaces the ad shown here without closing and reopening the
  // drawer, which would also throw away the refetch result.
  const [freshAd, setFreshAd] = useState(null);
  // An older version being viewed; null means the latest.
  const [versionAd, setVersionAd] = useState(null);
  const current = freshAd && adProp && freshAd.id === adProp.id ? freshAd : adProp;
  const ad = versionAd && current && versionAd.id === current.id ? versionAd : current;
  const viewVersion = async (no) => {
    if (!no) { setVersionAd(null); return; }
    try { setVersionAd(await adsGetAd(current.id, no)); } catch { /* keep the current view */ }
  };
  const handleRefetched = async (adId) => {
    try { setFreshAd(await adsGetAd(adId)); setVersionAd(null); } catch { /* keep the old view */ }
    onRefetched?.(adId);
  };
  const [analysisState, setAnalysisState] = useState("idle"); // idle | loading | done | error
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [history, setHistory] = useState(null); // null = not loaded, [] = empty, [...] = items
  const [historyExpanded, setHistoryExpanded] = useState(null); // id of expanded history item
  const [historyPage, setHistoryPage] = useState(0); // which page of history we show

  // Load analysis history on mount
  React.useEffect(() => {
    if (!ad?.id) return;
    adsGetAdAnalyses(ad.id).then(r => setHistory(r?.items || [])).catch(() => setHistory([]));
  }, [ad?.id]);

  const runAnalysis = async () => {
    if (!ad?.id) return;
    setAnalysisState("loading");
    setAnalysisError(null);
    try {
      const res = await adsAnalyzeAd(ad.id, brandId);
      const data = res?.analysis || null;
      setAnalysisData(data);
      setAnalysisState("done");
      // Prepend to history
      if (data) {
        setHistory(prev => [{ id: Date.now(), created_at: new Date().toISOString(), analysis: data }, ...(prev || [])]);
      }
    } catch (e) {
      setAnalysisError(e?.message || "Analysis failed.");
      setAnalysisState("error");
    }
  };

  if (loading) return <div style={{ padding: 24, color: "#6b7280", fontWeight: 700 }}>Loading…</div>;
  if (!ad) return <div style={{ padding: 24, color: "#9ca3af" }}>No ad selected.</div>;

  const creatives = Array.isArray(ad.creatives) ? ad.creatives : [];

  // A tile rather than a full-width row: eight short values now occupy two
  // rows instead of eight, which is what made the panel feel endless.
  const field = (label, value) => (
    <div style={{
      border: "1px solid #eef2f7", borderRadius: 8, padding: "7px 10px",
      background: "#fff", minWidth: 0,
    }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em" }}>
        {label}
        <OriginTag origin={ad.field_origins?.[TILE_KEY[label]]} />
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", wordBreak: "break-word", marginTop: 2 }}>
        {value || "-"}
      </div>
    </div>
  );

  const sectionHead = { margin: "0 0 10px", fontSize: 12, fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.06em", color: "#6b7280" };

  return (
    // Two tracks once there is room: the creative on the left, everything we
    // know on the right. Collapses to one column in a narrow drawer, creative
    // first, because that is what identifies the ad.
    <div style={{ padding: 20, display: "grid", gap: 20 }}>
      <div style={{
        display: "grid", gap: 20, alignItems: "start",
        gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))",
      }}>
      {/* Creatives: the ad itself */}
      {creatives.length > 0 && (
        <section style={{ position: "sticky", top: 0 }}>
          <h4 style={sectionHead}>Creatives ({creatives.length})</h4>
          <div style={{ display: "grid", gap: 14 }}>
            {creatives.map((c, i) => (
              <CreativeDetailItem key={c.id ?? i} creative={c} index={i} source={ad.source} ad={ad}
                                  onCaptured={() => onRefetched?.(ad.id)} />
            ))}
          </div>
        </section>
      )}

      <div style={{ display: "grid", gap: 18, minWidth: 0 }}>
      {ad.viewing_version && (
        <div style={{
          padding: "10px 13px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10,
          display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", fontSize: 12.5, color: "#92400e", fontWeight: 600,
        }}>
          <span>
            <b>Viewing v{ad.viewing_version.version_no} of {ad.viewing_version.of}</b>, as captured {fmtDateTime(ad.viewing_version.captured_at)}.
            {" "}Provider and calculated fields are shown as they were then; image reads and storage details are current.
          </span>
          <button className="btn-secondary" onClick={() => viewVersion(null)} style={{ padding: "4px 10px", borderRadius: 7, fontSize: 12 }}>
            Back to latest
          </button>
        </div>
      )}
      <CollectionSection
        ad={ad}
        brandId={brandId}
        onRefetched={handleRefetched}
        onShowRun={onShowRun}
        sectionHead={sectionHead}
      />

      <VersionsSection
        ad={current}
        sectionHead={sectionHead}
        viewingNo={ad.viewing_version?.version_no ?? null}
        onView={viewVersion}
      />

      {/* Ad info */}
      <section>
        <h4 style={sectionHead}>Ad info</h4>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 6, alignItems: "start" }}>
          {field("External ID", ad.external_ad_id)}
          {field("Source", providerMeta(ad.source).displayName)}
          {field("Advertiser", ad.advertiser_name
            ? <span title={ad.advertiser_ref}>{ad.advertiser_name}</span>
            : ad.advertiser_ref)}
          {field("Status", (
            <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
              {ad.status ? <Chip tone={toneFromStatus(ad.status)}>{ad.status}</Chip> : "-"}
              {ad.seen_in_runs === 1 && (
                <span title="First seen in the latest fetch" style={{ background: "#fef3c7", color: "#92400e", border: "1px solid #fde68a", borderRadius: 6, padding: "1px 7px", fontSize: 11, fontWeight: 800 }}>
                  ★ NEW
                </span>
              )}
            </span>
          ))}
          {field("Start date", fmtDate(ad.start_date))}
          {field(providerMeta(ad.source).endDateLabel, fmtDate(ad.end_date))}
          {/* Meta's headline numbers. Ad info showed Google's (impressions,
              days shown, topic) but none of Meta's, so EU reach - the one
              volume figure Meta publishes for European ads - and all of the
              targeting were only reachable inside the collapsed Advanced fields. */}
          {ad.eu_total_reach != null
            ? field("EU reach", Number(ad.eu_total_reach).toLocaleString())
            : null}
          {Array.isArray(ad.publisher_platforms) && ad.publisher_platforms.length
            ? field("Platforms", ad.publisher_platforms.join(", "))
            : null}
          {Array.isArray(ad.target_ages) && ad.target_ages.length
            ? field("Target ages", ad.target_ages.length === 2
                ? `${ad.target_ages[0]}–${ad.target_ages[1]}`
                : ad.target_ages.join(", "))
            : null}
          {ad.target_gender ? field("Target gender", ad.target_gender) : null}
          {Array.isArray(ad.target_locations) && ad.target_locations.length
            ? field("Target locations", ad.target_locations
                .map(l => (l?.excluded ? "not " : "") + (l?.name || ""))
                .filter(Boolean).join(", "))
            : null}
          {Array.isArray(ad.languages) && ad.languages.length
            ? field("Languages", ad.languages.map(l => String(l).toUpperCase()).join(", "))
            : null}
          {Array.isArray(ad.beneficiary_payers) && ad.beneficiary_payers.length
            ? field("Paid by", (() => {
                const p = ad.beneficiary_payers.find(x => x?.current) || ad.beneficiary_payers[0] || {};
                return p.payer && p.beneficiary && p.payer !== p.beneficiary
                  ? `${p.payer} (for ${p.beneficiary})`
                  : (p.payer || p.beneficiary || null);
              })())
            : null}
          {providerMeta(ad.source).showsTotalDaysShown && ad.total_days_shown != null
            ? field("Total days shown", String(ad.total_days_shown))
            : null}
          {providerMeta(ad.source).showsTopic && ad.topic
            ? field("Topic", <Chip tone="info">{ad.topic}</Chip>)
            : null}
          {providerMeta(ad.source).showsImpressionsRange
            && (ad.impressions_lower != null || ad.impressions_upper != null)
            ? field(
                "Impressions (range)",
                formatImpressionsRange(ad.impressions_lower, ad.impressions_upper),
              )
            : null}
          {providerMeta(ad.source).showsAudienceFlags
            && audienceFlagLabels(ad.audience_selection_flags).length > 0
            ? field(
                "Audience signals",
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {audienceFlagLabels(ad.audience_selection_flags).map((label) => (
                    <Chip key={label} tone="muted">
                      {label}
                    </Chip>
                  ))}
                </div>,
              )
            : null}
          {providerMeta(ad.source).showsRegionsSummary
            && regionsSummary(ad.region_metrics_json)
            ? field("Top regions", regionsSummary(ad.region_metrics_json))
            : null}
        </div>
      </section>

      {/* AI Analysis */}
      <section>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h4 style={{ ...sectionHead, margin: 0 }}>AI Analysis <OriginTag origin="ai" /></h4>
          {(analysisState === "idle" || analysisState === "error") && (
            <button className="btn-secondary" onClick={runAnalysis}
              style={{ fontSize: 12, padding: "5px 12px", borderRadius: 8 }}>
              ✨ Analyze
            </button>
          )}
          {analysisState === "done" && (
            <button className="btn-secondary" onClick={runAnalysis}
              style={{ fontSize: 11, padding: "4px 10px", borderRadius: 8, color: "#6b7280" }}>
              Re-analyze
            </button>
          )}
        </div>
        {analysisState === "idle" && (
          <div style={{ fontSize: 12, color: "#9ca3af", fontStyle: "italic" }}>
            Click Analyze to get AI insights on this ad's tone, messaging, and effectiveness.
          </div>
        )}
        {analysisState === "loading" && (
          <div style={{ color: "#6b7280", fontSize: 13, fontWeight: 700, padding: "8px 0" }}>Analyzing…</div>
        )}
        {analysisState === "error" && (
          <div style={{ color: "#dc2626", fontSize: 12, fontWeight: 700 }}>{analysisError}</div>
        )}
        {analysisState === "done" && analysisData && <AnalysisPanel data={analysisData} />}
      </section>

      {/* Analysis History */}
      {history && history.length > 0 && (() => {
        const totalPages = Math.ceil(history.length / HISTORY_PAGE_SIZE);
        const pageItems = history.slice(historyPage * HISTORY_PAGE_SIZE, (historyPage + 1) * HISTORY_PAGE_SIZE);
        return (
          <section>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <h4 style={{ ...sectionHead, margin: 0 }}>Analysis History ({history.length})</h4>
              {totalPages > 1 && (
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <button onClick={() => setHistoryPage(p => Math.max(0, p - 1))} disabled={historyPage === 0}
                    style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "2px 8px", fontSize: 12, background: "#fff", cursor: historyPage === 0 ? "default" : "pointer", color: historyPage === 0 ? "#d1d5db" : "#374151" }}>
                    ‹
                  </button>
                  <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 700 }}>{historyPage + 1}/{totalPages}</span>
                  <button onClick={() => setHistoryPage(p => Math.min(totalPages - 1, p + 1))} disabled={historyPage === totalPages - 1}
                    style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: "2px 8px", fontSize: 12, background: "#fff", cursor: historyPage === totalPages - 1 ? "default" : "pointer", color: historyPage === totalPages - 1 ? "#d1d5db" : "#374151" }}>
                    ›
                  </button>
                </div>
              )}
            </div>
            <div style={{ display: "grid", gap: 6 }}>
              {pageItems.map((item) => {
                const isNew = typeof item.id === "number" && item.id > 1e12;
                const expanded = historyExpanded === item.id;
                const dateLabel = item.created_at
                  ? new Date(item.created_at).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })
                  : "-";
                return (
                  <div key={item.id} style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
                    <button
                      onClick={() => setHistoryExpanded(expanded ? null : item.id)}
                      style={{
                        width: "100%", textAlign: "left", background: expanded ? "#f0fdf4" : "#f8fafc",
                        border: "none", padding: "8px 12px", cursor: "pointer",
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                      }}
                    >
                      <span style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>
                        {isNew ? "Just now" : dateLabel}
                      </span>
                      <span style={{ fontSize: 11, color: "#9ca3af" }}>{expanded ? "▲" : "▶"}</span>
                    </button>
                    {expanded && item.analysis && (
                      <div style={{ padding: "8px 12px" }}>
                        <AnalysisPanel data={item.analysis} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        );
      })()}

      {/* Every remaining field, so an ad's full record is reachable here
          rather than only through a CSV export. */}
      </div>
      </div>

      {/* Its own row, so it gets the whole width and the pair above can spread. */}
      <AdvancedFieldsPanel ad={ad} />
    </div>
  );
}

// ─── Bulk analysis modal ─────────────────────────────────────────────────────

function BulkAnalysisModal({ ads, brandId, onClose }) {
  // results: Map<adId, { status: "pending"|"analyzing"|"done"|"error", data, error }>
  const [results, setResults] = useState(() => {
    const m = new Map();
    ads.forEach(ad => m.set(ad.id, { status: "pending", data: null, error: null }));
    return m;
  });
  const [running, setRunning] = useState(false);
  const [started, setStarted] = useState(false);

  const runAll = async () => {
    setRunning(true);
    setStarted(true);
    // Mark all as analyzing immediately
    setResults(prev => {
      const m = new Map(prev);
      ads.forEach(ad => m.set(ad.id, { ...m.get(ad.id), status: "analyzing" }));
      return m;
    });
    // Analyze all in parallel
    await Promise.all(ads.map(async (ad) => {
      try {
        const res = await adsAnalyzeAd(ad.id, brandId, "bulk");
        setResults(prev => { const m = new Map(prev); m.set(ad.id, { status: "done", data: res?.analysis || null, error: null }); return m; });
      } catch (e) {
        setResults(prev => { const m = new Map(prev); m.set(ad.id, { status: "error", data: null, error: e?.message || "Failed" }); return m; });
      }
    }));
    setRunning(false);
  };

  const doneCount = [...results.values()].filter(r => r.status === "done").length;
  const errorCount = [...results.values()].filter(r => r.status === "error").length;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9000,
        background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 16, width: "100%", maxWidth: 680,
          maxHeight: "85vh", display: "flex", flexDirection: "column",
          boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
        }}
      >
        {/* Header */}
        <div style={{ padding: "18px 20px 14px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontWeight: 900, fontSize: 15, color: "#111827" }}>AI Analysis - {ads.length} ad{ads.length !== 1 ? "s" : ""}</div>
            {started && (
              <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700, marginTop: 2 }}>
                {doneCount + errorCount} / {ads.length} complete{errorCount > 0 ? ` · ${errorCount} failed` : ""}
                {running && " · analyzing…"}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {!started && (
              <button className="btn-primary" onClick={runAll} style={{ fontSize: 13, padding: "8px 16px", borderRadius: 8 }}>
                ✨ Start Analysis
              </button>
            )}
            <button className="btn-secondary" onClick={onClose} style={{ fontSize: 13, padding: "7px 14px", borderRadius: 8 }}>
              Close
            </button>
          </div>
        </div>

        {/* Results list */}
        <div style={{ overflowY: "auto", flex: 1, minHeight: 0, padding: 16, display: "grid", gap: 12 }}>
          {ads.map((ad) => {
            const r = results.get(ad.id);
            const firstCreative = (ad.creatives || [])[0] || {};
            const label = firstCreative.headline
              ? truncate(firstCreative.headline, 50)
              : `Ad #${ad.id}`;

            return (
              <div key={ad.id} style={{ border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
                {/* Ad header row */}
                <div style={{ padding: "10px 14px", background: "#f8fafc", display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: 13, color: "#111827" }}>{label}</div>
                    <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 700 }}>#{ad.id} · {ad.advertiser_ref || "-"}</div>
                  </div>
                  <div>
                    {r.status === "pending" && <span style={{ fontSize: 11, fontWeight: 800, color: "#9ca3af" }}>Pending</span>}
                    {r.status === "analyzing" && <span style={{ fontSize: 11, fontWeight: 800, color: "#6366f1" }}>Analyzing…</span>}
                    {r.status === "done" && <span style={{ fontSize: 11, fontWeight: 800, color: "#16a34a" }}>Done</span>}
                    {r.status === "error" && <span style={{ fontSize: 11, fontWeight: 800, color: "#dc2626" }}>Error</span>}
                  </div>
                </div>
                {/* Result */}
                {r.status === "done" && r.data && (
                  <div style={{ padding: "10px 14px" }}>
                    <AnalysisPanel data={r.data} />
                  </div>
                )}
                {r.status === "error" && (
                  <div style={{ padding: "10px 14px", fontSize: 12, color: "#dc2626", fontWeight: 700 }}>{r.error}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── List view ───────────────────────────────────────────────────────────────

function SortableTh({ field, sortField, sortDir, onSortChange }) {
  const active = sortField === field.key;
  return (
    <th
      onClick={() => field.sortable && onSortChange?.(field.key)}
      title={field.note || field.key}
      style={{
        textAlign: "left", padding: "9px 12px", whiteSpace: "nowrap",
        fontSize: 11, letterSpacing: ".04em", textTransform: "uppercase",
        color: active ? "#4338ca" : "#6b7280", fontWeight: 800,
        cursor: field.sortable ? "pointer" : "default",
        borderBottom: "1px solid #e5e7eb", background: "#fafafa",
      }}
    >
      {field.label}{active ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
      <OriginTag origin={field.origin} style={{ marginLeft: 5 }} />
    </th>
  );
}

/**
 * The list is a projection of the field catalogue: every column comes from the
 * picker, so adding a field to the registry makes it selectable here with no
 * change to this component.
 */
function ListView({
  ads, catalog, selectedKeys, onSelect, selectedIds, onToggle, onToggleAll,
  sortField, sortDir, onSortChange,
}) {
  const fields = (selectedKeys || [])
    .map(k => catalog.find(f => f.key === k))
    .filter(Boolean);

  const allChecked = ads.length > 0 && ads.every(a => selectedIds.has(a.id));
  const td = { padding: "9px 12px", fontSize: 13, borderTop: "1px solid #f1f5f9", verticalAlign: "top" };

  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, overflowX: "auto", background: "#fff" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ padding: "9px 12px", width: 36, background: "#fafafa", borderBottom: "1px solid #e5e7eb" }}>
              <input
                id="ads-list-select-all"
                type="checkbox"
                checked={allChecked}
                onChange={() => onToggleAll(ads)}
                style={{ cursor: "pointer", accentColor: "#6366f1" }}
              />
            </th>
            <th style={{ padding: "9px 12px", width: 62, background: "#fafafa", borderBottom: "1px solid #e5e7eb" }} />
            {fields.map(f => (
              <SortableTh key={f.key} field={f} sortField={sortField} sortDir={sortDir} onSortChange={onSortChange} />
            ))}
          </tr>
        </thead>
        <tbody>
          {ads.map(ad => {
            const cached = (ad.creatives || []).find(c => c.snapshot_cached_at);
            return (
              <tr
                key={ad.id}
                onClick={() => onSelect(ad.id)}
                style={{ cursor: "pointer", background: selectedIds.has(ad.id) ? "#eef2ff" : "transparent" }}
              >
                <td style={td} onClick={e => e.stopPropagation()}>
                  <input
                    id={`ads-list-row-${ad.id}`}
                    type="checkbox"
                    checked={selectedIds.has(ad.id)}
                    onChange={() => onToggle(ad.id)}
                    style={{ cursor: "pointer", accentColor: "#6366f1" }}
                  />
                </td>
                <td style={td}>
                  {cached
                    ? <SnapshotThumb
                        creativeId={cached.id}
                        imgStyle={{ width: 44, height: 44, objectFit: "cover", borderRadius: 6 }}
                        loadingNode={<span style={{ fontSize: 14, opacity: .3 }}>&#9203;</span>}
                        errorNode={<span style={{ fontSize: 14, opacity: .3 }}>&#128196;</span>}
                      />
                    : (isDirectImageUrl(ad.creatives?.[0]?.media_url) || ad.creatives?.[0]?.variation_meta_json?.media?.image_url)
                      ? <img src={isDirectImageUrl(ad.creatives[0].media_url) ? ad.creatives[0].media_url : ad.creatives[0].variation_meta_json.media.image_url}
                             alt="" style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 6 }} />
                      : <span style={{ fontSize: 11, color: "#c7cdd6" }}>no image</span>}
                </td>
                {fields.map(f => (
                  <td key={f.key} style={td}>
                    <span style={{ display: "block", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {truncate(formatFieldValue(readField(ad, f), f.kind), 90)}
                    </span>
                  </td>
                ))}
              </tr>
            );
          })}
          {ads.length === 0 && (
            <tr>
              <td colSpan={fields.length + 2} style={{ ...td, textAlign: "center", padding: 40, color: "#9ca3af", fontWeight: 700 }}>
                No ads found. Try adjusting filters or ingesting more data.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Stands in for the creative when there is no picture to show.
 *
 * Says which of two situations it is, because they call for different actions:
 * an ad whose screenshot has not been captured yet can still get one, while a
 * text-only ad never will and is not a gap in coverage. A bare document glyph
 * conflated the two and looked like a rendering failure.
 */
function CreativePlaceholder({ kind }) {
  const COPY = {
    uncaptured: "Image not captured yet",
    "no-media": "No image in this ad",
    video: "Video creative",
  };
  const isVideoKind = kind === "video";

  return (
    <div
      style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", gap: 9, color: "#94a3b8", padding: 10,
        textAlign: "center",
      }}
      title={COPY[kind] || COPY["no-media"]}
    >
      <svg
        width="42" height="42" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"
        strokeLinejoin="round" aria-hidden="true"
      >
        <rect x="3" y="5" width="18" height="14" rx="2" />
        {isVideoKind ? (
          <path d="M10 9.5l5 2.5-5 2.5z" />
        ) : (
          <>
            <circle cx="8.5" cy="10" r="1.4" />
            <path d="M3 16.5l4.5-4.5 3.5 3.5 3-3 6 6" />
            <path d="M4 20L20 4" opacity="0.55" />
          </>
        )}
      </svg>
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.1, lineHeight: 1.3 }}>
        {COPY[kind] || COPY["no-media"]}
      </span>
    </div>
  );
}

function MatrixCard({ ad, onSelect, isSelected, onToggle, onTypeDetected, catalog = [], selectedKeys = [] }) {
  const firstCreative = (ad.creatives || [])[0] || {};
  const [detectedType, setDetectedType] = useState(null);
  const [imgErr, setImgErr] = useState(false);
  const mediaUrl = firstCreative.media_url;
  const effectiveType = detectedType || firstCreative.creative_type;
  // Google's media URL is the image itself; a Meta ad's image is the CDN
  // link stored with its media at collection time (its media URL is a
  // facebook.com page this server is blocked from).
  const directSrc = isDirectImageUrl(mediaUrl) ? mediaUrl : (firstCreative.variation_meta_json?.media?.image_url || null);
  const isImage = !!directSrc;
  const isVideo = effectiveType === "video" || (mediaUrl && /\.(mp4|webm|ogg|mov)/i.test(mediaUrl));
  const cachedCreativeId = (ad.creatives || []).find(c => !!c.snapshot_cached_at)?.id;
  const handleTypeDetected = (cid, type) => { setDetectedType(type); onTypeDetected?.(cid, type); };

  const meta = providerMeta(ad.source);
  const advertiser = ad.advertiser_name || ad.advertiser_ref || "Unknown advertiser";
  const isCompetitor = ad.brand_role === "competitor";
  const originalUrl = _publicViewUrl({ source: ad.source, mediaUrl, externalAdId: ad.external_ad_id });
  const cardCount = Number(firstCreative.variation_meta_json?.card_count || 0);

  const showStatus = selectedKeys.includes("status");

  // Whole-creative view: a cover crop cuts the copy off the top and bottom of a
  // portrait ad, and reading the creative without opening it is the point here.
  const fitStyle = { maxWidth: "100%", maxHeight: "100%", objectFit: "contain" };

  return (
    <div
      onClick={() => onSelect(ad.id)}
      style={{
        border: isSelected ? "2px solid #6366f1" : "1px solid #e5e7eb",
        borderRadius: 14,
        background: "#fff",
        overflow: "hidden",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        transition: "box-shadow 0.15s, transform 0.1s",
        boxShadow: isSelected ? "0 0 0 3px rgba(99,102,241,0.15)" : "0 2px 8px rgba(15,23,42,0.05)",
        position: "relative",
      }}
      onMouseEnter={e => { if (!isSelected) { e.currentTarget.style.boxShadow = "0 8px 24px rgba(99,102,241,0.15)"; e.currentTarget.style.transform = "translateY(-1px)"; } }}
      onMouseLeave={e => { if (!isSelected) { e.currentTarget.style.boxShadow = "0 2px 8px rgba(15,23,42,0.05)"; e.currentTarget.style.transform = ""; } }}
    >
      {/* Header: who ran the ad, and where */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
        padding: "11px 14px", borderBottom: "1px solid #eef2f7",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <input
            type="checkbox"
            checked={isSelected}
            onClick={e => e.stopPropagation()}
            onChange={() => onToggle(ad.id)}
            style={{ width: 15, height: 15, cursor: "pointer", accentColor: "#6366f1", flexShrink: 0 }}
          />
          <span
            style={{ fontWeight: 800, fontSize: 13.5, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            title={advertiser}
          >
            {advertiser}
          </span>
          {isCompetitor && (
            <span style={{ background: "#fff7ed", color: "#9a3412", borderRadius: 5, padding: "1px 6px", fontSize: 10, fontWeight: 800, flexShrink: 0 }}>
              competitor
            </span>
          )}
        </div>
        <span style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          {ad.as_of_version && (
            <span
              title={`Version ${ad.as_of_version.version_no} of ${ad.as_of_version.of}, captured ${new Date(ad.as_of_version.captured_at).toLocaleString()}`}
              style={{ background: "#e0f2fe", color: "#075985", borderRadius: 6, padding: "3px 8px", fontSize: 11, fontWeight: 800 }}
            >
              v{ad.as_of_version.version_no}/{ad.as_of_version.of}
            </span>
          )}
          <span style={{ background: "#f1f5f9", color: "#475569", borderRadius: 6, padding: "3px 9px", fontSize: 11, fontWeight: 700 }}>
            {meta.label || ad.source}
          </span>
        </span>
      </div>

      {/* The creative, shown whole */}
      <div style={{
        height: 270, background: "#f1f5f9", display: "flex", alignItems: "center",
        justifyContent: "center", overflow: "hidden", position: "relative", padding: 18,
      }}>
        {cachedCreativeId
          ? <SnapshotThumb
              creativeId={cachedCreativeId}
              onTypeDetected={handleTypeDetected}
              imgStyle={{ ...fitStyle, borderRadius: 2 }}
              loadingNode={<div style={{ fontSize: 26, opacity: 0.2 }}>&#9203;</div>}
              errorNode={
                mediaUrl && isImage && !imgErr
                  ? <img src={directSrc} alt="" onError={() => setImgErr(true)} style={fitStyle} />
                  : isVideo ? <CreativePlaceholder kind="video" />
                  : <CreativePlaceholder kind={mediaUrl ? "uncaptured" : "no-media"} />
              }
            />
          : mediaUrl && isImage && !imgErr
          ? <img src={directSrc} alt="" onError={() => setImgErr(true)} style={fitStyle} />
          : isVideo
          ? <CreativePlaceholder kind="video" />
          : <CreativePlaceholder kind={mediaUrl ? "uncaptured" : "no-media"} />
        }
        {showStatus && ad.status && (
          <div style={{ position: "absolute", top: 10, right: 10 }}>
            <Chip tone={toneFromStatus(ad.status)}>{ad.status}</Chip>
          </div>
        )}
        {/* One snapshot covers the whole carousel, so say how many cards of
            copy sit behind it rather than implying the first card is all. */}
        {cardCount > 1 && (
          <div
            style={{
              position: "absolute", bottom: 10, left: 10,
              background: "rgba(15,23,42,0.72)", color: "#fff", borderRadius: 6,
              padding: "2px 8px", fontSize: 11, fontWeight: 800,
            }}
            title={`Carousel with ${cardCount} cards`}
          >
            {cardCount} cards
          </div>
        )}
      </div>

      {/* What the card shows is whatever the Fields picker selects, in that
          order. It used to be a fixed set, so the picker changed nothing here. */}
      <div style={{ padding: "13px 15px 0", display: "grid", gap: 7, flex: 1, alignContent: "start" }}>
        <CardFields ad={ad} catalog={catalog} selectedKeys={selectedKeys} />
      </div>

      {/* Footer */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
        borderTop: "1px solid #eef2f7", margin: "13px 15px 0", padding: "11px 0 13px",
      }}>
        <span style={{ fontSize: 12.5, fontWeight: 800, color: "#0f172a" }}>Details</span>
        {originalUrl && (
          <a
            href={originalUrl}
            target="_blank"
            rel="noreferrer"
            onClick={e => e.stopPropagation()}
            style={{ fontSize: 12.5, fontWeight: 700, color: "#2563eb", textDecoration: "underline" }}
          >
            Original ad &#8599;
          </a>
        )}
      </div>
    </div>
  );
}

// Shown by the card's frame already, so not repeated in its body.
const CARD_FRAME_KEYS = new Set(["advertiser_name", "status"]);

/**
 * The selected fields, rendered for a card.
 *
 * The two long text fields keep the card's reading layout (headline large,
 * body as a paragraph); everything else is a labelled row, and lists become
 * pills. An empty value is shown as a dash rather than hidden, so adding a
 * field visibly changes every card even where that ad has no value for it.
 */
function CardFields({ ad, catalog, selectedKeys }) {
  const fields = (selectedKeys || [])
    .map(k => catalog.find(f => f.key === k))
    .filter(Boolean)
    .filter(f => !CARD_FRAME_KEYS.has(f.key));

  if (!catalog.length) return null;
  if (!fields.length) {
    return (
      <div style={{ fontSize: 12, color: "#9ca3af", fontWeight: 600 }}>
        No fields selected. Use Fields to choose what cards show.
      </div>
    );
  }

  return fields.map(f => {
    const value = readField(ad, f);
    const empty = value === null || value === undefined || value === ""
      || (Array.isArray(value) && value.length === 0);

    if (f.key === "headline") {
      return (
        <div key={f.key} title={f.label} style={{ fontWeight: 800, fontSize: 16, color: empty ? "#cbd5e1" : "#0f172a", lineHeight: 1.3 }}>
          {empty ? "No headline" : truncate(String(value), 90)}
        </div>
      );
    }
    if (f.key === "primary_text") {
      return (
        <div key={f.key} title={f.label} style={{ fontSize: 13, color: empty ? "#cbd5e1" : "#475569", lineHeight: 1.5 }}>
          {empty ? "No body text" : truncate(String(value), 150)}
        </div>
      );
    }

    let body;
    if (empty) {
      body = <span style={{ color: "#cbd5e1" }}>—</span>;
    } else if (Array.isArray(value)) {
      body = (
        <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4 }}>
          {value.slice(0, 6).map((v, i) => (
            <span key={i} style={{ background: "#f1f5f9", color: "#334155", borderRadius: 6, padding: "1px 7px", fontSize: 11, fontWeight: 700 }}>
              {typeof v === "object" ? JSON.stringify(v) : String(v)}
            </span>
          ))}
          {value.length > 6 && <span style={{ fontSize: 11, color: "#94a3b8" }}>+{value.length - 6}</span>}
        </span>
      );
    } else {
      body = <span style={{ color: "#0f172a", fontWeight: 700 }}>{truncate(formatFieldValue(value, f.kind), 80)}</span>;
    }

    return (
      <div key={f.key} style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: 12.5, lineHeight: 1.4 }}>
        <span title={f.note || f.key} style={{ color: "#94a3b8", fontWeight: 700, flexShrink: 0, minWidth: 92 }}>
          {f.label}
          <OriginTag origin={f.origin} style={{ marginLeft: 4 }} />
        </span>
        <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{body}</span>
      </div>
    );
  });
}

/**
 * Select every ad on the current page of the matrix, the card view's
 * equivalent of the list's header checkbox. Shows a partial state when some
 * but not all are selected; clicking then selects the rest.
 */
function SelectAllBox({ ads, selectedIds, onToggleAll }) {
  const ref = React.useRef(null);
  const picked = ads.filter(a => selectedIds.has(a.id)).length;
  const all = picked === ads.length;
  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = picked > 0 && !all;
  }, [picked, all]);
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#374151", fontWeight: 700, cursor: "pointer" }}>
      <input
        ref={ref}
        id="ads-matrix-select-all"
        type="checkbox"
        checked={all}
        onChange={() => (picked > 0 && !all
          ? onToggleAll(ads.filter(a => !selectedIds.has(a.id)))
          : onToggleAll(ads))}
        style={{ width: 15, height: 15, cursor: "pointer", accentColor: "#6366f1" }}
      />
      Select all ({ads.length})
    </label>
  );
}

function MatrixView({ ads, onSelect, selectedIds, onToggle, onTypeDetected, perRow = 0, catalog = [], selectedKeys = [] }) {
  // perRow 0 means "fit as many as the width allows"; a number pins the count.
  const cols = perRow > 0
    ? `repeat(${perRow}, minmax(0, 1fr))`
    : "repeat(auto-fill, minmax(340px, 1fr))";
  return (
    <div style={{ display: "grid", gridTemplateColumns: cols, gap: 16 }}>
      {ads.map((ad) => (
        <MatrixCard
          key={ad.id}
          ad={ad}
          onSelect={onSelect}
          isSelected={selectedIds.has(ad.id)}
          onToggle={onToggle}
          onTypeDetected={onTypeDetected}
          catalog={catalog}
          selectedKeys={selectedKeys}
        />
      ))}
      {ads.length === 0 && (
        <div style={{ gridColumn: "1/-1", textAlign: "center", padding: 40, color: "#9ca3af", fontWeight: 700 }}>
          No ads found. Try adjusting filters or ingesting more data.
        </div>
      )}
    </div>
  );
}

// ─── Delete ads button (with confirmation) ──────────────────────────────────

function DeleteAdsButton({ count, onConfirm }) {
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{ background: "rgba(239,68,68,0.25)", border: "1px solid rgba(239,68,68,0.5)", color: "#fff", borderRadius: 8, padding: "8px 14px", cursor: "pointer", fontSize: 13, fontWeight: 800 }}
        title="Delete selected ads"
      >
        Delete
      </button>
      {open && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => !deleting && setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 14, padding: "28px 32px",
              maxWidth: 480, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
          >
            <div style={{ fontSize: 17, fontWeight: 800, color: "#111827", marginBottom: 14 }}>
              Delete {count} ad{count !== 1 ? "s" : ""}?
            </div>
            <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.7, marginBottom: 20 }}>
              Deleting the ads removes them from the brand and from the database.
              You will need to rerun the ingestion for this brand to refetch these ads.
              <br /><br />
              Previously finished analyses will not be impacted, but future continuation
              of those MAD Pilot chats can be affected since the model won't be able to
              find the deleted ads in context.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setOpen(false)}
                disabled={deleting}
                className="btn-secondary"
                style={{ padding: "8px 16px", fontSize: 13, fontWeight: 700 }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setDeleting(true);
                  try { await onConfirm(); } finally { setDeleting(false); setOpen(false); }
                }}
                disabled={deleting}
                style={{
                  background: "#ef4444", border: "none", color: "#fff",
                  borderRadius: 8, padding: "8px 20px", fontSize: 13,
                  fontWeight: 800, cursor: deleting ? "default" : "pointer",
                  opacity: deleting ? 0.7 : 1,
                }}
              >
                {deleting ? "Deleting..." : "Yes, delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Main export ─────────────────────────────────────────────────────────────

export default function AdsMatrixView({
  ads,
  loadingAds,
  adsTotal,
  adsOffset,
  adsLimit,
  nextPage,
  prevPage,
  onOffsetChange,
  viewMode,
  setViewMode,
  filterDateFrom, setFilterDateFrom,
  filterDateTo, setFilterDateTo,
  filterEndDateFrom, setFilterEndDateFrom,
  filterEndDateTo, setFilterEndDateTo,
  filterStatus, setFilterStatus,
  filterCreativeType, setFilterCreativeType,
  filterHeadline, setFilterHeadline,
  filterAdvertiser, setFilterAdvertiser,
  filterAdvertiserName, setFilterAdvertiserName,
  filterCta, setFilterCta,
  filterBody, setFilterBody,
  filterVisualPalette, setFilterVisualPalette,
  filterVisualFunnel, setFilterVisualFunnel,
  filterVisualClaim, setFilterVisualClaim,
  filterPeoplePresent, setFilterPeoplePresent,
  filterNewDays, setFilterNewDays,
  applyFilters,
  clearFilters,
  savedFilters,
  onSaveFilter,
  onApplySavedFilter,
  onDeleteSavedFilter,
  onSelectAd,
  onExportCsv,
  onPreviewSelected,
  onChatSelected,
  onDeleteAds,
  brandId,
  competitors,
  source,
  // Provider filters (ads12). These are referenced in the filter panel below;
  // without them in scope the panel throws on open.
  filterPublisherPlatform, setFilterPublisherPlatform,
  filterLanguage, setFilterLanguage,
  filterTargetGender, setFilterTargetGender,
  filterEuReachMin, setFilterEuReachMin,
  filterTargetDomain, setFilterTargetDomain,
  filterGooglePlatform, setFilterGooglePlatform,
  fieldFilters, setFieldFilters,
  filterAsOf, setFilterAsOf,
  filterNotUpdatedSince, setFilterNotUpdatedSince,
  filterRunningFrom, setFilterRunningFrom,
  filterRunningTo, setFilterRunningTo,
}) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterBrandRole, setFilterBrandRole] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  // Overrides stale creative_type from props after snapshot auto-detection
  // Snapshot auto-detection reports the real creative type back up.
  // Cards report the real creative type once a snapshot loads; the map only
  // exists to dedupe repeat reports, so just the setter is read.
  const [, setDetectedTypes] = useState({});
  const onTypeDetected = (creativeId, type) =>
    setDetectedTypes(prev => prev[creativeId] === type ? prev : { ...prev, [creativeId]: type });
  const [bulkAds, setBulkAds] = useState(null); // non-null → modal open
  // The field catalogue comes from the server so the picker, the filters and
  // the export cannot disagree about what a field is.
  const [catalog, setCatalog] = useState([]);
  const [fieldDefaults, setFieldDefaults] = useState({ list: [], matrix: [] });
  const [maxFields, setMaxFields] = useState(15);

  const LS_LIST_KEY = "ads.fields.list";
  const LS_MATRIX_KEY = "ads.fields.matrix";
  const LS_PERROW_KEY = "ads.matrix.perRow";

  const _readKeys = (storageKey) => {
    try {
      const rawVal = localStorage.getItem(storageKey);
      if (rawVal) {
        const arr = JSON.parse(rawVal);
        if (Array.isArray(arr) && arr.length) return arr;
      }
    } catch {}
    return null;
  };

  const [listKeys, setListKeys] = useState(() => _readKeys(LS_LIST_KEY) || []);
  const [matrixKeys, setMatrixKeys] = useState(() => _readKeys(LS_MATRIX_KEY) || []);
  const [perRow, setPerRow] = useState(() => {
    const v = parseInt(localStorage.getItem(LS_PERROW_KEY) || "0", 10);
    return Number.isFinite(v) ? v : 0;
  });
  const [downloading, setDownloading] = useState(false);
  // The running download's progress, e.g. {state: "preparing", ready: 120, total: 300}.
  const [downloadProgress, setDownloadProgress] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await adsListFields(source);
        if (cancelled) return;
        setCatalog(data.fields || []);
        setFieldDefaults(data.defaults || { list: [], matrix: [] });
        setMaxFields(data.max_display_fields || 15);
        // Only fall back to defaults when nothing was stored for this view.
        setListKeys(prev => (prev.length ? prev : (data.defaults?.list || [])));
        setMatrixKeys(prev => (prev.length ? prev : (data.defaults?.matrix || [])));
      } catch {
        // A failed catalogue leaves the views empty rather than guessing.
      }
    })();
    return () => { cancelled = true; };
  }, [source]);

  const activeKeys = viewMode === "matrix" ? matrixKeys : listKeys;
  const setActiveKeys = (keys) => {
    if (viewMode === "matrix") {
      setMatrixKeys(keys);
      try { localStorage.setItem(LS_MATRIX_KEY, JSON.stringify(keys)); } catch {}
    } else {
      setListKeys(keys);
      try { localStorage.setItem(LS_LIST_KEY, JSON.stringify(keys)); } catch {}
    }
  };

  const downloadSelectedImages = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setDownloading(true);
    setDownloadProgress(null);
    try {
      let job = await adsStartImageDownload(ids);
      // Images not stored yet are fetched first, a few seconds each.
      while (job && !["ready", "failed"].includes(job.state)) {
        setDownloadProgress(job);
        await new Promise(r => setTimeout(r, 1500));
        job = await adsImageDownloadStatus(job.job_id);
      }
      if (!job || job.state === "failed") {
        throw new Error(job?.error || "The images could not be prepared.");
      }
      const blob = await adsImageDownloadFile(job.job_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ad_images_${job.included || ids.length}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      window.alert(e?.message || "Could not download the images.");
    } finally {
      setDownloading(false);
      setDownloadProgress(null);
    }
  };
  const downloadLabel = !downloadProgress || downloadProgress.state === "queued"
    ? "Preparing…"
    : downloadProgress.state === "zipping"
      ? "Zipping…"
      : `Preparing images ${downloadProgress.ready || 0} of ${downloadProgress.total || "?"}`;
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [pickerOpen, setPickerOpen] = useState(false);

  // Counted rather than flagged, so the button says how much is narrowing
  // the list. The named legacy values are included for links that still set
  // them directly, such as a run's contribution.
  const activeFilterCount =
    Object.keys(compactFieldFilters(fieldFilters)).length
    + [filterDateFrom, filterDateTo, filterEndDateFrom, filterEndDateTo, filterStatus,
       filterCreativeType, filterHeadline, filterAdvertiser, filterAdvertiserName, filterCta,
       filterBody, filterVisualPalette, filterVisualFunnel, filterVisualClaim,
       filterPeoplePresent, filterNewDays, filterBrandRole,
       filterAsOf, filterNotUpdatedSince, filterRunningFrom, filterRunningTo].filter(Boolean).length;
  const hasActiveFilters = activeFilterCount > 0;

  // ── sort ──
  const onSortChange = (field) => {
    if (sortField !== field) { setSortField(field); setSortDir("asc"); }
    else if (sortDir === "asc") { setSortDir("desc"); }
    else { setSortField(null); setSortDir("asc"); }
  };
  const getSortValue = (ad, field) => {
    const c = (ad.creatives || [])[0] || {};
    switch (field) {
      case "external_id":    return ad.external_ad_id || "";
      case "advertiser":     return ad.advertiser_ref || "";
      case "advertiser_name": return ad.advertiser_name || "";
      case "status":         return ad.status || "";
      case "start_date":     return ad.start_date || "";
      case "end_date":       return ad.end_date || "";
      case "headline":       return c.headline || "";
      default:               return "";
    }
  };
  const filteredByBrand = filterBrandRole
    ? ads.filter(ad => filterBrandRole === "primary"
        ? ad.brand_role === "primary"
        : String(ad.brand_id) === filterBrandRole)
    : ads;
  const sortedAds = sortField
    ? [...filteredByBrand].sort((a, b) => {
        const va = getSortValue(a, sortField);
        const vb = getSortValue(b, sortField);
        return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      })
    : filteredByBrand;

  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleAll = (pageAds) => {
    const allSelected = pageAds.every(a => selectedIds.has(a.id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) pageAds.forEach(a => next.delete(a.id));
      else pageAds.forEach(a => next.add(a.id));
      return next;
    });
  };

  const openBulkAnalysis = () => {
    const selected = ads.filter(a => selectedIds.has(a.id));
    if (selected.length === 0) return;
    setBulkAds(selected);
  };

  return (
    <div style={{ display: "grid", gap: 14 }}>
      {/* What these counts can be trusted to say, stated next to them */}
      <AdsCoveragePanel
        brandId={brandId}
        source={source}
        includeCompetitors={(competitors || []).length > 0}
      />

      {/* Toolbar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
          <button
            className={hasActiveFilters ? "btn-primary" : "btn-secondary"}
            onClick={() => setFiltersOpen(x => !x)}
            style={{ padding: "7px 14px", borderRadius: 8, fontSize: 13, display: "flex", gap: 6, alignItems: "center" }}
          >
            🔍 Filters {hasActiveFilters && <span style={{ background: "rgba(255,255,255,0.3)", borderRadius: 4, padding: "0 5px", fontSize: 11 }}>{activeFilterCount}</span>}
          </button>
          {hasActiveFilters && (
            <button className="btn-secondary" onClick={clearFilters}
              style={{ padding: "7px 12px", borderRadius: 8, fontSize: 12, color: "#dc2626" }}>
              ✕ Clear
            </button>
          )}
          <div style={{ position: "relative" }}>
            <button
              className="btn-secondary"
              onClick={() => setPickerOpen(x => !x)}
              style={{ padding: "7px 14px", borderRadius: 8, fontSize: 13, display: "flex", gap: 6, alignItems: "center" }}
              title="Choose which fields are shown"
            >
              &#8862; Fields ({activeKeys.length}) &#9662;
            </button>
            {pickerOpen && (
              <FieldPicker
                catalog={catalog}
                selected={activeKeys}
                onChange={setActiveKeys}
                view={viewMode === "matrix" ? "matrix" : "list"}
                max={maxFields}
                defaults={viewMode === "matrix" ? fieldDefaults.matrix : fieldDefaults.list}
                onClose={() => setPickerOpen(false)}
              />
            )}
          </div>
          {viewMode === "matrix" && filteredByBrand.length > 0 && (
            <SelectAllBox ads={filteredByBrand} selectedIds={selectedIds} onToggleAll={toggleAll} />
          )}
          {viewMode === "matrix" && (
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#374151", fontWeight: 700 }}>
              Per row
              <select
                id="ads-matrix-per-row"
                value={perRow}
                onChange={e => {
                  const v = parseInt(e.target.value, 10) || 0;
                  setPerRow(v);
                  try { localStorage.setItem(LS_PERROW_KEY, String(v)); } catch {}
                }}
                style={{ padding: "6px 8px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 12.5 }}
              >
                <option value={0}>Auto</option>
                {[2, 3, 4, 5, 6, 8].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          )}
          {selectedIds.size > 0 && (
            <button
              className="btn-secondary"
              onClick={downloadSelectedImages}
              disabled={downloading}
              style={{ padding: "7px 12px", borderRadius: 8, fontSize: 13 }}
              title="Download the captured images for the selected ads"
            >
              {downloading ? downloadLabel : `\u2b07 Images (${selectedIds.size})`}
            </button>
          )}
          {onExportCsv && (
            <>
              <button className="btn-secondary" onClick={() => onExportCsv({ fields: activeKeys })}
                style={{ padding: "7px 12px", borderRadius: 8, fontSize: 13 }}
                title="Export the rows on screen, with the fields currently shown">
                &#11015; CSV ({activeKeys.length} fields)
              </button>
              <button className="btn-secondary" onClick={() => onExportCsv({ allFields: true })}
                style={{ padding: "7px 12px", borderRadius: 8, fontSize: 13 }}
                title="Export the same rows with every field we hold">
                &#11015; CSV, all fields
              </button>
            </>
          )}
        </div>
        <Pagination
          offset={adsOffset}
          limit={adsLimit}
          total={adsTotal}
          onPrev={prevPage}
          onNext={nextPage}
          onOffsetChange={onOffsetChange}
        />
      </div>

      {/* Filter bar */}
      {filtersOpen && (
        <FilterBar
          filterDateFrom={filterDateFrom} setFilterDateFrom={setFilterDateFrom}
          filterDateTo={filterDateTo} setFilterDateTo={setFilterDateTo}
          filterEndDateFrom={filterEndDateFrom} setFilterEndDateFrom={setFilterEndDateFrom}
          filterEndDateTo={filterEndDateTo} setFilterEndDateTo={setFilterEndDateTo}
          filterStatus={filterStatus} setFilterStatus={setFilterStatus}
          filterCreativeType={filterCreativeType} setFilterCreativeType={setFilterCreativeType}
          filterHeadline={filterHeadline} setFilterHeadline={setFilterHeadline}
          filterAdvertiser={filterAdvertiser} setFilterAdvertiser={setFilterAdvertiser}
          filterAdvertiserName={filterAdvertiserName} setFilterAdvertiserName={setFilterAdvertiserName}
          filterCta={filterCta} setFilterCta={setFilterCta}
          filterBody={filterBody} setFilterBody={setFilterBody}
          filterVisualPalette={filterVisualPalette} setFilterVisualPalette={setFilterVisualPalette}
          filterVisualFunnel={filterVisualFunnel} setFilterVisualFunnel={setFilterVisualFunnel}
          filterVisualClaim={filterVisualClaim} setFilterVisualClaim={setFilterVisualClaim}
          filterPeoplePresent={filterPeoplePresent} setFilterPeoplePresent={setFilterPeoplePresent}
          filterNewDays={filterNewDays} setFilterNewDays={setFilterNewDays}
          filterBrandRole={filterBrandRole} setFilterBrandRole={setFilterBrandRole}
          competitors={competitors}
          onApply={() => { applyFilters(); setFiltersOpen(false); }}
          filterPublisherPlatform={filterPublisherPlatform} setFilterPublisherPlatform={setFilterPublisherPlatform}
          filterLanguage={filterLanguage} setFilterLanguage={setFilterLanguage}
          filterTargetGender={filterTargetGender} setFilterTargetGender={setFilterTargetGender}
          filterEuReachMin={filterEuReachMin} setFilterEuReachMin={setFilterEuReachMin}
          filterTargetDomain={filterTargetDomain} setFilterTargetDomain={setFilterTargetDomain}
          filterGooglePlatform={filterGooglePlatform} setFilterGooglePlatform={setFilterGooglePlatform}
          onClear={() => { clearFilters(); setFiltersOpen(false); }}
          savedFilters={savedFilters}
          onSaveFilter={onSaveFilter}
          onApplySavedFilter={(sf) => { onApplySavedFilter(sf); setFiltersOpen(false); }}
          onDeleteSavedFilter={onDeleteSavedFilter}
          catalog={catalog}
          fieldFilters={fieldFilters}
          setFieldFilters={setFieldFilters}
          brandId={brandId}
          source={source}
          filterAsOf={filterAsOf}
          setFilterAsOf={setFilterAsOf}
          filterNotUpdatedSince={filterNotUpdatedSince}
          setFilterNotUpdatedSince={setFilterNotUpdatedSince}
          filterRunningFrom={filterRunningFrom}
          setFilterRunningFrom={setFilterRunningFrom}
          filterRunningTo={filterRunningTo}
          setFilterRunningTo={setFilterRunningTo}
        />
      )}

      {/* When the list shows past versions, say so: every card below is how
          that ad looked then, not now. */}
      {!loadingAds && ads.some(a => a.as_of_version) && (
        <div style={{
          padding: "9px 13px", background: "#f0f9ff", border: "1px solid #bae6fd",
          borderRadius: 10, fontSize: 12.5, color: "#075985", fontWeight: 600, lineHeight: 1.5,
        }}>
          <b>Showing each ad as it was on {filterAsOf ? new Date(filterAsOf).toLocaleDateString() : "the chosen date"}</b>
          {": the latest version captured by then, which can differ from ad to ad. "}
          Ads first fetched after that date are not shown. Other filters match each ad's current values.
        </div>
      )}

      {/* Loading */}
      {loadingAds && (
        <div style={{ textAlign: "center", padding: 32, color: "#6b7280", fontWeight: 700 }}>
          Loading ads…
        </div>
      )}

      {/* View */}
      {!loadingAds && (
        viewMode === "matrix"
          ? <MatrixView
              ads={filteredByBrand}
              onSelect={onSelectAd}
              selectedIds={selectedIds}
              onToggle={toggleSelect}
              onTypeDetected={onTypeDetected}
              perRow={perRow}
              catalog={catalog}
              selectedKeys={matrixKeys}
            />
          : <ListView
              ads={sortedAds}
              catalog={catalog}
              selectedKeys={listKeys}
              onSelect={onSelectAd}
              selectedIds={selectedIds}
              onToggle={toggleSelect}
              onToggleAll={toggleAll}
              sortField={sortField}
              sortDir={sortDir}
              onSortChange={onSortChange}
            />
      )}

      {/* Selection hint - only when ads are loaded and nothing selected */}
      {!loadingAds && ads.length > 0 && selectedIds.size === 0 && (
        <div style={{ textAlign: "center", fontSize: 12, color: "#c4b5fd", fontWeight: 700, padding: "2px 0 4px" }}>
          Tip: check ads to analyze them with AI or discuss with MAD Pilot
        </div>
      )}

      {/* Bottom pagination */}
      {!loadingAds && ads.length > 0 && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Pagination
            offset={adsOffset}
            limit={adsLimit}
            total={adsTotal}
            onPrev={prevPage}
            onNext={nextPage}
            onOffsetChange={onOffsetChange}
          />
        </div>
      )}

      {/* Bulk analysis modal */}
      {bulkAds && (
        <BulkAnalysisModal
          ads={bulkAds}
          brandId={brandId}
          onClose={() => setBulkAds(null)}
        />
      )}

      {/* Floating selection action bar - slides up when ads are selected */}
      {selectedIds.size > 0 && (
        <div style={{
          position: "fixed",
          bottom: 0, left: 0, right: 0,
          zIndex: 8000,
          background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
          color: "#fff",
          padding: "14px 28px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          boxShadow: "0 -4px 32px rgba(79,70,229,0.4)",
        }}>
          <div style={{ fontWeight: 900, fontSize: 14, minWidth: 120 }}>
            {selectedIds.size} ad{selectedIds.size !== 1 ? "s" : ""} selected
          </div>
          <button
            onClick={() => setSelectedIds(new Set())}
            style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", color: "#fff", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontWeight: 800 }}
          >
            ✕ Clear
          </button>
          <div style={{ marginLeft: "auto", display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              onClick={openBulkAnalysis}
              style={{ background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.35)", color: "#fff", borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 900 }}
            >
              ✨ Analyze
            </button>
            {onChatSelected && (
              <button
                onClick={() => onChatSelected(ads.filter(a => selectedIds.has(a.id)))}
                style={{ background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.35)", color: "#fff", borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 900 }}
              >
                💬 MAD Pilot
              </button>
            )}
            {onPreviewSelected && (
              <button
                onClick={() => onPreviewSelected(ads.filter(a => selectedIds.has(a.id)))}
                style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", color: "#fff", borderRadius: 8, padding: "8px 14px", cursor: "pointer", fontSize: 13, fontWeight: 800 }}
                title="Capture screenshots for selected ads"
              >
                📸 Preview
              </button>
            )}
            {onExportCsv && (
              <button
                onClick={onExportCsv}
                style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", color: "#fff", borderRadius: 8, padding: "8px 14px", cursor: "pointer", fontSize: 13, fontWeight: 800 }}
                title="Export filtered ads to CSV"
              >
                ⬇ Export CSV
              </button>
            )}
            {onDeleteAds && (
              <DeleteAdsButton
                count={selectedIds.size}
                onConfirm={async () => {
                  await onDeleteAds([...selectedIds]);
                  setSelectedIds(new Set());
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}